import { BedrockAgentRuntimeClient, InvokeAgentCommand } from '@aws-sdk/client-bedrock-agent-runtime';
import { AnalysisResult } from './geminiService';

export interface BedrockAgent {
  id: string;
  alias: string;
  name: string;
  subjects: string[];
  description: string;
}

export interface QuestionPaper {
  title: string;
  subject: string;
  duration: string;
  totalMarks: number;
  sections: QuestionSection[];
  instructions: string[];
  generatedBy: string;
  timestamp: string;
  answerKey?: {
    solutions: {
      questionNumber: number;
      solution: string;
      markingScheme: string;
    }[];
  };
  metadata?: {
    generatedBy: string;
    timestamp: string;
    difficulty: string;
    estimatedTime: string;
  };
}

export interface QuestionSection {
  sectionName: string;
  instructions: string;
  questions: Question[];
  totalMarks: number;
}

export interface Question {
  questionNumber: number;
  question: string;
  marks: number;
  questionType: 'MCQ' | 'Short Answer' | 'Long Answer' | 'Numerical' | 'Essay' | 'Graph' | 'Diagram' | 'Equation';
  options?: string[];
  correctAnswer?: string;
  answerKey?: string;
  bloomLevel?: 'Remember' | 'Understand' | 'Apply' | 'Analyze' | 'Evaluate' | 'Create';
  mathContent?: {
    latex?: string;
    equations?: string[];
    integrals?: string[];
    graphs?: GraphDefinition[];
    tables?: TableData[];
  };
  visualContent?: {
    diagrams?: DiagramData[];
    charts?: ChartData[];
    images?: string[];
  };
}

export interface GraphDefinition {
  type: 'function' | 'bar' | 'line' | 'pie' | 'scatter' | 'histogram';
  title: string;
  data: any;
  mermaidCode?: string;
  mathFunction?: string;
  domain?: [number, number];
  range?: [number, number];
}

export interface TableData {
  headers: string[];
  rows: string[][];
  caption?: string;
}

export interface DiagramData {
  type: 'flowchart' | 'circuit' | 'geometry' | 'biological' | 'chemical';
  mermaidCode?: string;
  description: string;
}

export interface ChartData {
  type: 'bar' | 'line' | 'pie' | 'scatter';
  title: string;
  labels: string[];
  datasets: {
    label: string;
    data: number[];
    backgroundColor?: string[];
    borderColor?: string;
  }[];
}

// Enhanced FlashCard interfaces for Bedrock agent
export interface MermaidDiagram {
  type: 'flowchart' | 'graph' | 'mindmap' | 'sequence' | 'class' | 'state' | 'pie' | 'gitgraph';
  code: string;
  description: string;
}

export interface VisualElements {
  hasFormula: boolean;
  hasChart: boolean;
  hasDiagram: boolean;
  complexityLevel: 'simple' | 'moderate' | 'complex';
}

export interface BedrockFlashCard {
  id: string;
  front: string;
  back: string;
  topic: string;
  difficulty: 'easy' | 'medium' | 'hard';
  type: 'definition' | 'concept' | 'formula' | 'process' | 'diagram' | 'example';
  mermaidDiagram?: MermaidDiagram;
  visualElements: VisualElements;
  learningObjective: string;
  hints: string[];
  relatedConcepts: string[];
}


export interface BedrockFlashCardSet {
  title: string;
  subject: string;
  cards: BedrockFlashCard[];
  totalCards: number;
  estimatedStudyTime: string;
  difficultyDistribution: {
    easy: number;
    medium: number;
    hard: number;
  };
  topicCoverage: string[];
  mermaidTypes: string[];
  generatedBy: string;
  timestamp: string;
}

class BedrockService {
  private client: BedrockAgentRuntimeClient | null = null;
  private agents: BedrockAgent[] = [];
  private isBedrockAvailable: boolean = false;

  constructor() {
    // Read credentials from environment variables only
    const accessKeyId = process.env.REACT_APP_AWS_ACCESS_KEY_ID;
    const secretAccessKey = process.env.REACT_APP_AWS_SECRET_ACCESS_KEY;
    const region = process.env.REACT_APP_AWS_REGION || 'eu-north-1';

    console.log('🔧 AWS Bedrock Configuration Check:');
    console.log('📍 Region:', region);
    console.log('🔑 Access Key ID:', accessKeyId ? `${accessKeyId.slice(0, 6)}...${accessKeyId.slice(-4)}` : '❌ NOT SET');
    console.log('🔒 Secret Access Key:', secretAccessKey ? `${secretAccessKey.slice(0, 6)}...${secretAccessKey.slice(-4)}` : '❌ NOT SET');
    
    // Detailed environment variable check
    console.log('🌍 Environment Variables:');
    console.log('  REACT_APP_AWS_ACCESS_KEY_ID:', process.env.REACT_APP_AWS_ACCESS_KEY_ID ? '✅ SET' : '❌ MISSING');
    console.log('  REACT_APP_AWS_SECRET_ACCESS_KEY:', process.env.REACT_APP_AWS_SECRET_ACCESS_KEY ? '✅ SET' : '❌ MISSING');
    console.log('  REACT_APP_AWS_REGION:', process.env.REACT_APP_AWS_REGION || 'using default: eu-north-1');

    // Initialize AWS Bedrock client only if credentials are available
    try {
      if (accessKeyId && secretAccessKey) {
        console.log('🚀 Initializing Bedrock client...');
        this.client = new BedrockAgentRuntimeClient({
          region: region,
          credentials: {
            accessKeyId: accessKeyId,
            secretAccessKey: secretAccessKey,
          },
        });
        
        this.isBedrockAvailable = true;
        console.log('✅ AWS Bedrock client successfully initialized');
        console.log('🎯 Bedrock will be used for question paper generation');
      } else {
        console.error('❌ AWS Bedrock credentials MISSING - falling back to local generation');
        console.error('🔍 Required environment variables:');
        console.error('   REACT_APP_AWS_ACCESS_KEY_ID:', !accessKeyId ? '❌ MISSING' : '✅ OK');
        console.error('   REACT_APP_AWS_SECRET_ACCESS_KEY:', !secretAccessKey ? '❌ MISSING' : '✅ OK');
        console.error('💡 Please set these environment variables to use Bedrock');
        this.isBedrockAvailable = false;
      }
    } catch (error) {
      console.error('❌ CRITICAL: Failed to initialize AWS Bedrock client:', error);
      this.isBedrockAvailable = false;
    }

    // Debug: Log environment variables for agent configuration
    console.log('🤖 Bedrock Agent Configuration:');
    console.log('  FLASHCARD_GENERATOR:', process.env.REACT_APP_BEDROCK_AGENT_FLASHCARD_GENERATOR ? '✅ SET' : '❌ MISSING');
    console.log('  MATHEMATICS:', process.env.REACT_APP_BEDROCK_AGENT_MATHEMATICS ? '✅ SET' : '❌ MISSING');
    console.log('  DATA_SCIENCE:', process.env.REACT_APP_BEDROCK_AGENT_DATA_SCIENCE ? '✅ SET' : '❌ MISSING');
    console.log('  GENERAL:', process.env.REACT_APP_BEDROCK_AGENT_GENERAL ? '✅ SET' : '❌ MISSING');
    console.log('  AGENT_ALIAS:', process.env.REACT_APP_BEDROCK_AGENT_ALIAS ? '✅ SET' : '❌ MISSING');

    // Define available agents with their subject specializations based on your AWS agents
    this.agents = [
      {
        id: process.env.REACT_APP_BEDROCK_AGENT_FLASHCARD_GENERATOR || 'FLASHCARD_AGENT_ID',
        alias: process.env.REACT_APP_BEDROCK_AGENT_ALIAS || 'TSTALIASID',
        name: 'Flashcard Generator Specialist',
        subjects: ['flashcards', 'visual learning', 'interactive learning', 'mermaid diagrams', 'educational content'],
        description: 'Specialized in creating interactive flashcards with visual diagrams and comprehensive learning aids'
      },
      {
        id: process.env.REACT_APP_BEDROCK_AGENT_MATHEMATICS || 'TXGRUACVNW',
        alias: process.env.REACT_APP_BEDROCK_AGENT_ALIAS || 'TSTALIASID',
        name: 'Mathematics & Statistics Specialist',
        subjects: ['mathematics', 'math', 'statistics', 'algebra', 'calculus', 'geometry', 'trigonometry', 'probability', 'data analysis'],
        description: 'Specialized in mathematical concepts, statistical analysis, and quantitative problem solving'
      },
      {
        id: process.env.REACT_APP_BEDROCK_AGENT_DATA_SCIENCE || 'HASXTWDVJM',
        alias: process.env.REACT_APP_BEDROCK_AGENT_ALIAS || 'TSTALIASID',
        name: 'Data Science Specialist',
        subjects: ['data science', 'machine learning', 'artificial intelligence', 'data analysis', 'python', 'r programming', 'statistics'],
        description: 'Expert in data science concepts, machine learning algorithms, and data analytics'
      },
      {
        id: process.env.REACT_APP_BEDROCK_AGENT_MECHANICAL_ENGINEERING || 'W24FHTF0DQ',
        alias: process.env.REACT_APP_BEDROCK_AGENT_ALIAS || 'TSTALIASID',
        name: 'Mechanical Engineering Specialist',
        subjects: ['mechanical engineering', 'thermodynamics', 'mechanics', 'fluid mechanics', 'materials science', 'manufacturing'],
        description: 'Specialized in mechanical engineering principles, design, and manufacturing processes'
      },
      {
        id: process.env.REACT_APP_BEDROCK_AGENT_ELECTRICAL_ENGINEERING || 'EORCKPWW4Y',
        alias: process.env.REACT_APP_BEDROCK_AGENT_ALIAS || 'TSTALIASID',
        name: 'Electrical Engineering Specialist',
        subjects: ['electrical engineering', 'electronics', 'circuits', 'power systems', 'control systems', 'signal processing'],
        description: 'Expert in electrical circuits, power systems, and electronic device design'
      },
      {
        id: process.env.REACT_APP_BEDROCK_AGENT_CIVIL_ENGINEERING || '2HO5NGNUZB',
        alias: process.env.REACT_APP_BEDROCK_AGENT_ALIAS || 'TSTALIASID',
        name: 'Civil Engineering Specialist',
        subjects: ['civil engineering', 'structural engineering', 'construction', 'materials', 'geotechnical', 'transportation'],
        description: 'Specialized in civil infrastructure, structural design, and construction management'
      },
      {
        id: process.env.REACT_APP_BEDROCK_AGENT_CHEMISTRY || 'TKVRZCRSZA',
        alias: process.env.REACT_APP_BEDROCK_AGENT_ALIAS || 'TSTALIASID',
        name: 'Chemical Engineering Specialist',
        subjects: ['chemical engineering', 'chemistry', 'process engineering', 'reaction engineering', 'thermodynamics', 'mass transfer'],
        description: 'Expert in chemical processes, reaction engineering, and industrial chemistry'
      },
      {
        id: process.env.REACT_APP_BEDROCK_AGENT_ECONOMICS || 'TS69D3B8VB',
        alias: process.env.REACT_APP_BEDROCK_AGENT_ALIAS || 'TSTALIASID',
        name: 'Economics Specialist',
        subjects: ['economics', 'microeconomics', 'macroeconomics', 'finance', 'business economics', 'econometrics'],
        description: 'Specialized in economic theories, market analysis, and financial concepts'
      },
      {
        id: process.env.REACT_APP_BEDROCK_AGENT_COMPUTER_SCIENCE || 'G0LHLLA8NW',
        alias: process.env.REACT_APP_BEDROCK_AGENT_ALIAS || 'TSTALIASID',
        name: 'Computer Science Specialist',
        subjects: ['computer science', 'programming', 'algorithms', 'data structures', 'software engineering', 'databases', 'networking'],
        description: 'Expert in programming, algorithms, software development, and computer systems'
      },
      {
        id: process.env.REACT_APP_BEDROCK_AGENT_COLLABORATOR || 'TKVRZCRSZA',
        alias: process.env.REACT_APP_BEDROCK_AGENT_ALIAS || 'TSTALIASID',
        name: 'Interdisciplinary Collaborator',
        subjects: ['interdisciplinary', 'collaboration', 'project management', 'research methodology', 'technical writing'],
        description: 'Specialized in collaborative projects and interdisciplinary question paper creation'
      },
      {
        id: process.env.REACT_APP_BEDROCK_AGENT_GENERAL || 'TKVRZCRSZA',
        alias: process.env.REACT_APP_BEDROCK_AGENT_ALIAS || 'TSTALIASID',
        name: 'General Academic Specialist',
        subjects: ['general knowledge', 'current affairs', 'general studies', 'aptitude', 'reasoning', 'comprehensive'],
        description: 'Covers general academic topics and comprehensive question paper creation'
      }
    ];

    // Filter out agents with invalid IDs and log details
    console.log('🔍 Validating Bedrock agents...');
    this.agents = this.agents.filter(agent => {
      const isValid = agent.id && agent.id.trim() !== '' && agent.alias && agent.alias.trim() !== '';
      if (!isValid) {
        console.warn(`❌ Invalid agent filtered out: ${agent.name}`);
        console.warn(`   ID: ${agent.id || 'MISSING'}`);
        console.warn(`   Alias: ${agent.alias || 'MISSING'}`);
      } else {
        console.log(`✅ Valid agent: ${agent.name} (ID: ${agent.id.substring(0, 8)}...)`);
      }
      return isValid;
    });

    console.log(`🤖 Successfully loaded ${this.agents.length} valid Bedrock agents`);
    
    if (this.agents.length === 0) {
      console.error('❌ CRITICAL: No valid Bedrock agents found!');
      console.error('💡 Please check your environment variables for agent IDs and aliases');
    } else {
      console.log('📋 Available agents for question generation:');
      this.agents.forEach(agent => {
        console.log(`   • ${agent.name}: ${agent.subjects.join(', ')}`);
      });
    }
  }

  // Test method to validate agent configuration
  async testAgentConnection(agent: BedrockAgent): Promise<boolean> {
    if (!this.client || !this.isBedrockAvailable) {
      return false;
    }

    try {
      const command = new InvokeAgentCommand({
        agentId: agent.id.trim(),
        agentAliasId: agent.alias.trim(),
        sessionId: `test-session-${Date.now()}`,
        inputText: "Test connection",
      });

      await this.client.send(command);
      return true;
    } catch (error) {
      console.error(`Failed to connect to agent ${agent.name}:`, error);
      return false;
    }
  }

  selectBestAgent(analysisResult: AnalysisResult): BedrockAgent {
    const subject = analysisResult.analysis.subject.toLowerCase();
    const topics = analysisResult.analysis.topics.map(topic => topic.toLowerCase());
    
    console.log('Selecting agent for subject:', subject);
    console.log('Available agents:');
    this.agents.forEach(agent => {
      console.log(`- ${agent.name}: ID=${agent.id}, subjects=${agent.subjects.join(', ')}`);
    });
    
    // Calculate relevance score for each agent
    const agentScores = this.agents.map(agent => {
      let score = 0;
      
      // Check if subject matches agent's specialization
      agent.subjects.forEach(agentSubject => {
        if (subject.includes(agentSubject) || agentSubject.includes(subject)) {
          score += 10;
        }
        
        // Check if any topics match
        topics.forEach(topic => {
          if (topic.includes(agentSubject) || agentSubject.includes(topic)) {
            score += 5;
          }
        });
      });
      
      return { agent, score };
    });
    
    // Sort by score and return the best match
    agentScores.sort((a, b) => b.score - a.score);
    
    console.log('Agent scores:', agentScores.map(as => ({ name: as.agent.name, score: as.score, id: as.agent.id })));
    
    // If no good match found, use general agent
    const bestMatch = agentScores[0];
    if (bestMatch.score === 0) {
      const fallbackAgent = this.agents.find(agent => agent.name.includes('General')) || this.agents[0];
      console.log('No good match found, using fallback agent:', fallbackAgent?.name, 'ID:', fallbackAgent?.id);
      if (!fallbackAgent) {
        throw new Error('No valid agents available');
      }
      return fallbackAgent;
    }
    
    console.log('Selected best agent:', bestMatch.agent.name, 'ID:', bestMatch.agent.id, 'Score:', bestMatch.score);
    return bestMatch.agent;
  }

  async generateQuestionPaper(
    analysisResult: AnalysisResult, 
    selectedAgent: BedrockAgent
  ): Promise<QuestionPaper> {
    // Always try Bedrock first, even if isBedrockAvailable is false, to get actual error
    console.log('🚀 Attempting to use AWS Bedrock for question paper generation...');
    console.log('🔍 Bedrock availability flag:', this.isBedrockAvailable);
    console.log('🤖 Selected agent:', selectedAgent.name, 'ID:', selectedAgent.id);
    console.log('🔑 Client initialized:', !!this.client);
    
    // Diagnostic check - log environment variables (safely)
    console.log('🔧 Environment check:');
    console.log('- AWS_ACCESS_KEY_ID exists:', !!process.env.REACT_APP_AWS_ACCESS_KEY_ID);
    console.log('- AWS_SECRET_ACCESS_KEY exists:', !!process.env.REACT_APP_AWS_SECRET_ACCESS_KEY);
    console.log('- AWS_REGION:', process.env.REACT_APP_AWS_REGION || 'eu-north-1');

    try {
      // Validate agent ID
      if (!selectedAgent.id || selectedAgent.id.trim() === '') {
        throw new Error(`Invalid agent ID: ${selectedAgent.id}. Agent: ${selectedAgent.name}`);
      }

      // Validate agent alias
      if (!selectedAgent.alias || selectedAgent.alias.trim() === '') {
        throw new Error(`Invalid agent alias: ${selectedAgent.alias}. Agent: ${selectedAgent.name}`);
      }

      // Prepare the enhanced prompt for the Bedrock agent
      const enhancedPrompt = this.buildEnhancedPrompt(analysisResult);
      
      const command = new InvokeAgentCommand({
        agentId: selectedAgent.id.trim(),
        agentAliasId: selectedAgent.alias.trim(),
        sessionId: `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        inputText: enhancedPrompt,
      });

      console.log('🚀 Invoking Bedrock agent...');
      console.log('🤖 Agent Name:', selectedAgent.name);
      console.log('🆔 Agent ID:', selectedAgent.id);
      console.log('🏷️ Agent Alias:', selectedAgent.alias);
      console.log('🔗 Session ID:', command.input.sessionId);
      console.log('📝 Prompt Length:', enhancedPrompt.length, 'characters');
      console.log('📋 Prompt Preview:', enhancedPrompt.substring(0, 200) + '...');

      if (!this.client) {
        throw new Error('❌ Bedrock client is not initialized - credentials likely missing or invalid');
      }

      console.log('⏳ Sending request to Bedrock...');
      const response = await this.client.send(command);
      console.log('📨 Received response from Bedrock');
      
      // Process the response with detailed logging
      let generatedContent = '';
      if (response.completion) {
        console.log('🔄 Processing response chunks...');
        let chunkCount = 0;
        for await (const chunk of response.completion) {
          chunkCount++;
          if (chunk.chunk?.bytes) {
            const text = new TextDecoder().decode(chunk.chunk.bytes);
            generatedContent += text;
            console.log(`📦 Chunk ${chunkCount}: ${text.length} characters`);
          }
        }
        console.log(`✅ Processed ${chunkCount} response chunks`);
      } else {
        console.warn('⚠️ No completion object in Bedrock response');
      }

      console.log('📊 Generated Content Stats:');
      console.log('   Length:', generatedContent.length, 'characters');
      console.log('   Preview:', generatedContent.substring(0, 300) + '...');
      console.log('   Has JSON markers:', generatedContent.includes('{') && generatedContent.includes('}') ? '✅' : '❌');
      
      if (!generatedContent || generatedContent.trim().length === 0) {
        console.error('❌ CRITICAL: No content generated by Bedrock agent!');
        console.error('💡 This usually indicates:');
        console.error('   • Agent is not responding properly');
        console.error('   • Prompt is too long or malformed');
        console.error('   • Agent configuration issues');
        console.error('🔄 Falling back to local generation...');
        return this.createFallbackQuestionPaperFromAnalysis(analysisResult);
      }

      console.log('✅ Content generated successfully - proceeding to parse...');
      // Parse the generated content into a structured question paper
      const questionPaper = this.parseQuestionPaper(generatedContent, analysisResult, selectedAgent);
      
      console.log('🎉 Successfully generated question paper with Bedrock!');
      return questionPaper;
      
    } catch (error) {
      console.error('❌ Error generating question paper with Bedrock:', error);
      
      // Log detailed error information for debugging
      if (error instanceof Error) {
        console.error('📋 Detailed Bedrock Error:', {
          message: error.message,
          name: error.name,
          stack: error.stack,
          agentId: selectedAgent.id,
          agentAlias: selectedAgent.alias,
          agentName: selectedAgent.name
        });
      }
      
      // Check if this is a credential/permission issue
      if (error instanceof Error && error.message.includes('UnauthorizedOperation')) {
        console.error('🔑 AWS Bedrock Authorization Error - Check your credentials and permissions');
      } else if (error instanceof Error && error.message.includes('ResourceNotFoundException')) {
        console.error('🤖 Bedrock Agent Not Found - Check if agent ID exists and is deployed');
      } else if (error instanceof Error && error.message.includes('ValidationException')) {
        console.error('📋 Bedrock Validation Error - Check request format');
      }
      
      // Always fallback to local generation for any Bedrock error
      console.warn('🔄 Falling back to local question paper generation due to Bedrock error');
      console.warn('⚠️ This fallback should only be used when Bedrock is unavailable!');
      return this.createFallbackQuestionPaperFromAnalysis(analysisResult);
    }
  }

  // Create a fallback question paper when Bedrock is not available
  private createFallbackQuestionPaperFromAnalysis(analysisResult: AnalysisResult): QuestionPaper {
    const analysis = analysisResult.analysis;
    
    // Use parsed requirements if available, otherwise create default structure
    let requirements: any = analysis.parsedRequirements;
    
    // If no parsed requirements, try to use paper pattern
    if (!requirements && analysisResult.paperPattern) {
      const pattern = analysisResult.paperPattern;
      requirements = {
        examDuration: pattern.examDuration || '3 hours',
        totalMarks: pattern.totalMarks ? parseInt(pattern.totalMarks.toString()) : 100,
        sections: pattern.sections?.map((section: any) => ({
          name: section.sectionName || section.name || 'Section A',
          questions: section.questions || 5,
          marksPerQuestion: section.marksPerQuestion || 20,
          totalMarks: section.totalMarks || 100,
          questionType: section.questionType || 'Short Answer'
        })) || [{
          name: 'Section A',
          questions: 5,
          marksPerQuestion: 20,
          totalMarks: 100,
          questionType: 'Short Answer'
        }]
      };
    }
    
    // Final fallback to default structure
    if (!requirements) {
      requirements = {
        examDuration: '3 hours',
        totalMarks: 100,
        sections: [{
          name: 'Section A',
          questions: 5,
          marksPerQuestion: 20,
          totalMarks: 100,
          questionType: 'Short Answer'
        }]
      };
    }
    
    console.log('📋 Using requirements for fallback generation:', requirements);
    
    return {
      title: `${analysis.subject} Question Paper`,
      subject: analysis.subject,
      duration: requirements.examDuration || '3 hours',
      totalMarks: requirements.totalMarks || 100,
      timestamp: new Date().toISOString(),
      generatedBy: 'Fallback Generator (Following User Requirements)',
      instructions: [
        'Read all instructions carefully before attempting the questions.',
        'Attempt all questions.',
        'Show all calculations clearly.',
        'Use diagrams where appropriate.',
        'Manage your time effectively.'
      ],
      sections: (requirements.sections || []).map((section: any, sectionIndex: number) => ({
        sectionName: section.name || `Section ${String.fromCharCode(65 + sectionIndex)}`,
        instructions: section.instructions || `Answer all questions from this section. Each question carries ${section.marksPerQuestion || 20} marks.`,
        totalMarks: section.totalMarks || 100,
        questions: this.generateFallbackQuestions(analysis, section)
      }))
    };
  }

  // Helper method to check if we have a question bank for a subject
  private hasSubjectQuestionBank(subject: string): boolean {
    const subjectLower = subject.toLowerCase();
    return ['mathematics', 'science', 'physics', 'chemistry', 'biology', 'computer science', 'engineering', 'economics', 'history', 'geography'].includes(subjectLower);
  }

  private buildSubjectQuestionBank(subject: string, difficulty: string, questionType: string): any[] {
    const subjectLower = subject.toLowerCase();
    const isMultipleChoice = questionType.toLowerCase().includes('mcq') || questionType.toLowerCase().includes('multiple choice');
    
    // Comprehensive question banks for different subjects
    const questionBanks: Record<string, any[]> = {
      mathematics: isMultipleChoice ? [
        {
          question: "What is the derivative of sin(x)?",
          options: ["A) cos(x)", "B) -cos(x)", "C) sin(x)", "D) -sin(x)"],
          correctAnswer: "A"
        },
        {
          question: "Which of the following is the quadratic formula?",
          options: ["A) x = (-b ± √(b²-4ac)) / 2a", "B) x = -b ± √(b²-4ac)", "C) x = b ± √(b²-4ac) / 2a", "D) x = (-b ± √(b²+4ac)) / 2a"],
          correctAnswer: "A"
        },
        {
          question: "What is the value of π (pi) approximately?",
          options: ["A) 3.14159", "B) 2.71828", "C) 1.41421", "D) 1.61803"],
          correctAnswer: "A"
        },
        {
          question: "In a right triangle, which theorem relates the sides?",
          options: ["A) Pythagorean theorem", "B) Law of cosines", "C) Law of sines", "D) Binomial theorem"],
          correctAnswer: "A"
        },
        {
          question: "What is the integral of x²?",
          options: ["A) x³/3 + C", "B) 2x + C", "C) x³ + C", "D) 3x² + C"],
          correctAnswer: "A"
        }
      ] : [
        { question: "Explain the fundamental theorem of calculus and its applications." },
        { question: "Derive the quadratic formula from the general quadratic equation." },
        { question: "Discuss the properties and applications of complex numbers." },
        { question: "Analyze the convergence of infinite series using various tests." },
        { question: "Explain linear transformations and their matrix representations." }
      ],
      
      science: isMultipleChoice ? [
        {
          question: "What is the chemical symbol for gold?",
          options: ["A) Au", "B) Ag", "C) Fe", "D) Cu"],
          correctAnswer: "A"
        },
        {
          question: "Which force keeps planets in orbit around the sun?",
          options: ["A) Gravitational force", "B) Electromagnetic force", "C) Nuclear force", "D) Friction force"],
          correctAnswer: "A"
        },
        {
          question: "What is the speed of light in vacuum?",
          options: ["A) 3×10⁸ m/s", "B) 3×10⁶ m/s", "C) 3×10¹⁰ m/s", "D) 3×10⁴ m/s"],
          correctAnswer: "A"
        },
        {
          question: "Which organelle is known as the powerhouse of the cell?",
          options: ["A) Mitochondria", "B) Nucleus", "C) Ribosome", "D) Lysosome"],
          correctAnswer: "A"
        },
        {
          question: "What is the most abundant gas in Earth's atmosphere?",
          options: ["A) Nitrogen", "B) Oxygen", "C) Carbon dioxide", "D) Argon"],
          correctAnswer: "A"
        }
      ] : [
        { question: "Explain the theory of evolution and evidence supporting it." },
        { question: "Describe the structure and function of DNA in heredity." },
        { question: "Analyze the greenhouse effect and its impact on climate change." },
        { question: "Discuss the principles of quantum mechanics and their applications." },
        { question: "Explain the process of photosynthesis and its significance." }
      ],
      
      history: isMultipleChoice ? [
        {
          question: "In which year did World War II end?",
          options: ["A) 1945", "B) 1944", "C) 1946", "D) 1943"],
          correctAnswer: "A"
        },
        {
          question: "Who was the first President of the United States?",
          options: ["A) George Washington", "B) Thomas Jefferson", "C) John Adams", "D) Benjamin Franklin"],
          correctAnswer: "A"
        },
        {
          question: "The Renaissance period began in which country?",
          options: ["A) Italy", "B) France", "C) England", "D) Germany"],
          correctAnswer: "A"
        },
        {
          question: "Which empire was ruled by Julius Caesar?",
          options: ["A) Roman Empire", "B) Greek Empire", "C) Persian Empire", "D) Byzantine Empire"],
          correctAnswer: "A"
        },
        {
          question: "The Industrial Revolution started in which century?",
          options: ["A) 18th century", "B) 17th century", "C) 19th century", "D) 16th century"],
          correctAnswer: "A"
        }
      ] : [
        { question: "Analyze the causes and consequences of the French Revolution." },
        { question: "Discuss the impact of colonialism on indigenous populations." },
        { question: "Examine the factors that led to the fall of the Roman Empire." },
        { question: "Evaluate the significance of the Enlightenment on modern democracy." },
        { question: "Explain the social and economic changes during the Industrial Revolution." }
      ],
      
      geography: isMultipleChoice ? [
        {
          question: "Which is the largest continent by area?",
          options: ["A) Asia", "B) Africa", "C) North America", "D) Europe"],
          correctAnswer: "A"
        },
        {
          question: "What is the deepest ocean trench?",
          options: ["A) Mariana Trench", "B) Puerto Rico Trench", "C) Peru-Chile Trench", "D) Kermadec Trench"],
          correctAnswer: "A"
        },
        {
          question: "Which river is the longest in the world?",
          options: ["A) Nile", "B) Amazon", "C) Mississippi", "D) Yangtze"],
          correctAnswer: "A"
        },
        {
          question: "What causes earthquakes?",
          options: ["A) Tectonic plate movement", "B) Ocean currents", "C) Wind patterns", "D) Magnetic field changes"],
          correctAnswer: "A"
        },
        {
          question: "Which climate zone is characterized by hot, dry summers and mild, wet winters?",
          options: ["A) Mediterranean", "B) Tropical", "C) Temperate", "D) Arctic"],
          correctAnswer: "A"
        }
      ] : [
        { question: "Explain the formation and characteristics of different types of mountains." },
        { question: "Analyze the impact of climate change on global weather patterns." },
        { question: "Discuss the water cycle and its importance in Earth's ecosystem." },
        { question: "Examine the causes and effects of urbanization on the environment." },
        { question: "Describe the formation of different landforms by geological processes." }
      ],
      
      // General fallback questions for any subject
      general: isMultipleChoice ? [
        {
          question: "What is the primary method of scientific inquiry?",
          options: ["A) Scientific method", "B) Observation only", "C) Hypothesis testing", "D) Data collection"],
          correctAnswer: "A"
        },
        {
          question: "Which skill is most important for critical thinking?",
          options: ["A) Analysis", "B) Memorization", "C) Speed", "D) Creativity"],
          correctAnswer: "A"
        },
        {
          question: "What characterizes effective communication?",
          options: ["A) Clarity and understanding", "B) Complex vocabulary", "C) Lengthy explanations", "D) Technical jargon"],
          correctAnswer: "A"
        },
        {
          question: "In problem-solving, what should be the first step?",
          options: ["A) Understanding the problem", "B) Finding solutions", "C) Implementing changes", "D) Evaluating results"],
          correctAnswer: "A"
        },
        {
          question: "What is essential for lifelong learning?",
          options: ["A) Curiosity and adaptability", "B) Perfect memory", "C) Formal education only", "D) Avoiding challenges"],
          correctAnswer: "A"
        }
      ] : [
        { question: "Analyze the importance of critical thinking in decision-making processes." },
        { question: "Discuss the role of technology in modern education and its implications." },
        { question: "Examine the principles of effective leadership in various contexts." },
        { question: "Evaluate the impact of globalization on cultural diversity." },
        { question: "Explain the concept of sustainable development and its challenges." }
      ]
    };
    
    // Enhanced subject matching logic
    let selectedQuestions = questionBanks.general; // Default fallback
    
    // Check for specific subject matches
    if (subjectLower.includes('math') || subjectLower.includes('calculus') || subjectLower.includes('algebra')) {
      selectedQuestions = questionBanks.mathematics;
    } else if (subjectLower.includes('science') || subjectLower.includes('physics') || subjectLower.includes('chemistry') || subjectLower.includes('biology')) {
      selectedQuestions = questionBanks.science;
    } else if (subjectLower.includes('history') || subjectLower.includes('historical')) {
      selectedQuestions = questionBanks.history;
    } else if (subjectLower.includes('geography') || subjectLower.includes('geographical')) {
      selectedQuestions = questionBanks.geography;
    }
    
    // If we don't have enough questions, expand the bank by creating variations
    const minQuestions = 20; // Ensure we have enough questions
    if (selectedQuestions.length < minQuestions) {
      selectedQuestions = this.expandQuestionBank(selectedQuestions, subjectLower, isMultipleChoice, minQuestions);
    }
    
    return selectedQuestions;
  }

  private expandQuestionBank(baseQuestions: any[], subject: string, isMultipleChoice: boolean, targetCount: number): any[] {
    const expandedQuestions = [...baseQuestions];
    
    // Additional question pools for different domains
    const additionalMCQs = [
      {
        question: "What is the foundation of logical reasoning?",
        options: ["A) Evidence and systematic thinking", "B) Personal opinions", "C) Random guessing", "D) Emotional responses"],
        correctAnswer: "A"
      },
      {
        question: "Which approach is most effective for learning new concepts?",
        options: ["A) Active engagement and practice", "B) Passive reading only", "C) Memorization without understanding", "D) Avoiding difficult topics"],
        correctAnswer: "A"
      },
      {
        question: "What characterizes quality research?",
        options: ["A) Systematic methodology and peer review", "B) Personal beliefs only", "C) Unverified sources", "D) Biased conclusions"],
        correctAnswer: "A"
      },
      {
        question: "In academic writing, what is most important?",
        options: ["A) Clear arguments with evidence", "B) Complex vocabulary", "C) Length over quality", "D) Personal anecdotes only"],
        correctAnswer: "A"
      },
      {
        question: "What drives innovation in any field?",
        options: ["A) Creative problem-solving", "B) Following established patterns", "C) Avoiding risks", "D) Maintaining status quo"],
        correctAnswer: "A"
      },
      {
        question: "Which skill is essential for collaboration?",
        options: ["A) Active listening and respect", "B) Dominating discussions", "C) Working in isolation", "D) Avoiding feedback"],
        correctAnswer: "A"
      },
      {
        question: "What is the key to effective time management?",
        options: ["A) Prioritization and planning", "B) Multitasking everything", "C) Procrastination", "D) Random scheduling"],
        correctAnswer: "A"
      },
      {
        question: "In decision-making, what should be considered first?",
        options: ["A) Available information and consequences", "B) First impressions", "C) Popular opinions", "D) Immediate desires"],
        correctAnswer: "A"
      },
      {
        question: "What promotes intellectual growth?",
        options: ["A) Questioning assumptions and seeking knowledge", "B) Accepting everything blindly", "C) Avoiding challenges", "D) Staying in comfort zone"],
        correctAnswer: "A"
      },
      {
        question: "Which factor is crucial for academic success?",
        options: ["A) Consistent effort and reflection", "B) Natural talent alone", "C) Avoiding difficult subjects", "D) Minimal preparation"],
        correctAnswer: "A"
      }
    ];

    const additionalOpenEnded = [
      { question: "Analyze the relationship between theory and practice in your field of study." },
      { question: "Evaluate the role of ethics in professional decision-making." },
      { question: "Compare different methodologies for approaching complex problems." },
      { question: "Discuss the importance of interdisciplinary approaches in modern research." },
      { question: "Examine the impact of cultural context on knowledge formation." },
      { question: "Assess the role of technology in shaping contemporary learning." },
      { question: "Analyze the balance between specialization and broad knowledge." },
      { question: "Evaluate the importance of peer collaboration in academic work." },
      { question: "Discuss strategies for maintaining objectivity in research." },
      { question: "Examine the process of knowledge validation in your discipline." }
    ];

    // Add questions until we reach target count
    const additionalPool = isMultipleChoice ? additionalMCQs : additionalOpenEnded;
    let poolIndex = 0;
    
    while (expandedQuestions.length < targetCount && poolIndex < additionalPool.length) {
      expandedQuestions.push(additionalPool[poolIndex]);
      poolIndex++;
    }
    
    // If we still need more questions, create variations of existing ones
    if (expandedQuestions.length < targetCount) {
      const variations = this.createQuestionVariations(baseQuestions, subject, isMultipleChoice, targetCount - expandedQuestions.length);
      expandedQuestions.push(...variations);
    }
    
    return expandedQuestions;
  }

  private createQuestionVariations(baseQuestions: any[], subject: string, isMultipleChoice: boolean, needed: number): any[] {
    const variations = [];
    const templates = isMultipleChoice ? [
      {
        template: "Which principle is fundamental to {subject}?",
        options: ["A) Systematic understanding and application", "B) Random approaches", "C) Avoiding complexity", "D) Superficial knowledge"],
        correctAnswer: "A"
      },
      {
        template: "What distinguishes effective {subject} practice?",
        options: ["A) Evidence-based methods", "B) Intuition only", "C) Trial and error", "D) Avoiding verification"],
        correctAnswer: "A"
      },
      {
        template: "In {subject}, what promotes deep understanding?",
        options: ["A) Analysis and synthesis", "B) Memorization alone", "C) Avoiding questions", "D) Surface-level study"],
        correctAnswer: "A"
      }
    ] : [
      { template: "Analyze the core principles that guide effective practice in {subject}." },
      { template: "Evaluate the methodology used for advancing knowledge in {subject}." },
      { template: "Discuss the interconnections between different concepts in {subject}." }
    ];

    for (let i = 0; i < needed && i < templates.length; i++) {
      const template = templates[i % templates.length];
      if (isMultipleChoice && 'options' in template) {
        variations.push({
          question: template.template.replace('{subject}', subject || 'this field'),
          options: template.options,
          correctAnswer: template.correctAnswer
        });
      } else {
        variations.push({
          question: template.template.replace('{subject}', subject || 'this field')
        });
      }
    }

    return variations;
  }

  private generateBasicQuestion(subject: string, index: number, questionType: string): any {
    const isMultipleChoice = questionType.toLowerCase().includes('mcq') || questionType.toLowerCase().includes('multiple choice');
    
    if (isMultipleChoice) {
      return {
        question: `Which fundamental concept is most important in ${subject}?`,
        options: [
          "A) Understanding basic principles",
          "B) Memorizing facts only",
          "C) Avoiding practical application",
          "D) Ignoring theoretical foundations"
        ],
        correctAnswer: "A"
      };
    } else {
      return {
        question: `Discuss the key principles and applications of ${subject} in modern contexts.`
      };
    }
  }

  private generateFallbackQuestions(analysis: any, section: any): Question[] {
    const questions: Question[] = [];
    const extractedQuestions = analysis.extractedQuestions || [];
    const numberOfQuestions = section.questions || 5;
    const marksPerQuestion = section.marksPerQuestion || 20;
    const questionType = section.questionType || 'Short Answer';
    
    // Get subject context for intelligent question generation
    const subject = analysis.subject || 'General Knowledge';
    const difficulty = analysis.difficulty || 'medium';
    const topics = analysis.topics || [];
    
    console.log(`📝 Generating ${numberOfQuestions} questions for ${subject}`);
    console.log(`📊 Available extracted questions: ${extractedQuestions.length}`);
    console.log(`📚 Available topics from document: ${topics.join(', ')}`);
    console.log(`🔍 Analysis object keys:`, Object.keys(analysis));
    
    // Extract more topics from content if available
    let contentTopics: string[] = [];
    if (analysis.parsedRequirements && analysis.parsedRequirements.extractedContent) {
      contentTopics = this.extractTopicsFromContent(analysis.parsedRequirements.extractedContent);
      console.log(`📝 Extracted content topics: ${contentTopics.join(', ')}`);
    }
    
    // Combine and filter meaningful topics
    const combinedTopics = topics.concat(contentTopics);
    const uniqueTopicsSet = new Set(combinedTopics);
    const uniqueTopicsArray = Array.from(uniqueTopicsSet);
    const allTopics: string[] = [];
    
    for (let i = 0; i < uniqueTopicsArray.length; i++) {
      const topic = uniqueTopicsArray[i];
      if (topic && 
          typeof topic === 'string' &&
          topic !== 'Based on uploaded content' && 
          topic !== 'General Knowledge' &&
          topic.length > 3 && 
          !topic.toLowerCase().includes('content') &&
          !topic.toLowerCase().includes('general') &&
          !topic.toLowerCase().includes('material')) {
        allTopics.push(topic);
      }
    }
    
    console.log(`🎯 Final topic list for question generation: ${allTopics.join(', ')}`);
    
    // Check if we have any actual content from the document
    const hasRealContent = extractedQuestions.length > 0 || allTopics.length > 0;
    
    console.log(`📋 Has real document content: ${hasRealContent}`);
    
    if (!hasRealContent) {
      console.warn('⚠️ No real document content found - Bedrock should have been used but failed!');
      console.log('🔧 Analysis data:', {
        subject: analysis.subject,
        difficulty: analysis.difficulty,
        topics: analysis.topics,
        extractedQuestions: extractedQuestions.slice(0, 3),
        contentSnippet: analysis.content ? analysis.content.substring(0, 200) : 'No content'
      });
    }
    
    // Generate questions with improved logic
    for (let i = 0; i < numberOfQuestions; i++) {
      const questionNumber = i + 1;
      let questionData;
      
      if (i < extractedQuestions.length) {
        // Use extracted question from uploaded document
        const cleanedQuestion = extractedQuestions[i]
          .replace(/^\d+[a-z]?\)\s*/, '') // Remove question numbering
          .replace(/\(\d+\s*m(arks?)?\)/gi, '') // Remove marks notation
          .replace(/\[\d+\s*m(arks?)?\]/gi, '') // Remove square bracket marks
          .trim();
        
        console.log(`📖 Using extracted question ${i + 1}: ${cleanedQuestion.substring(0, 50)}...`);
        
        questionData = {
          question: cleanedQuestion,
          options: null,
          correctAnswer: null
        };
      } else if (allTopics.length > 0) {
        // Generate questions based on actual topics from the document
        questionData = this.generateTopicBasedQuestion(allTopics, subject, i, questionType, difficulty);
        console.log(`📝 Generated topic-based question ${i + 1}: ${questionData.question.substring(0, 50)}...`);
      } else if (this.hasSubjectQuestionBank(subject)) {
        // Use subject-specific question bank if available
        const questionBank = this.buildSubjectQuestionBank(subject, difficulty, questionType);
        questionData = questionBank[i % questionBank.length];
        console.log(`📚 Used subject bank question ${i + 1} for ${subject}`);
      } else {
        // Generate contextual questions that are better than generic ones
        questionData = this.generateContextualQuestion(subject, i, questionType, difficulty);
        console.log(`🎯 Generated contextual question ${i + 1}: ${questionData.question.substring(0, 50)}...`);
      }
      
      // Ensure we have valid question data
      if (!questionData) {
        questionData = this.generateDefaultQuestion(subject, i, questionType);
        console.log(`🔧 Used default question ${i + 1}: ${questionData.question.substring(0, 50)}...`);
      }
      
      // Create question object
      const question: Question = {
        questionNumber: questionNumber,
        question: questionData.question,
        marks: marksPerQuestion,
        questionType: questionType as Question['questionType'],
        bloomLevel: this.getBloomLevel(difficulty, questionType)
      };
      
      // Add MCQ options if needed
      if ((questionType.toLowerCase().includes('mcq') || questionType.toLowerCase().includes('multiple choice')) && questionData.options) {
        question.options = questionData.options;
        question.correctAnswer = questionData.correctAnswer;
      }
      
      questions.push(question);
    }
    
    console.log(`✅ Generated ${questions.length} questions (${extractedQuestions.length} from document, ${allTopics.length > 0 ? 'topic-based' : 'contextual'} for remainder)`);
    return questions;
  }

  private generateDocumentBasedQuestion(topics: string[], subject: string, index: number, questionType: string, difficulty: string): any {
    const isMultipleChoice = questionType.toLowerCase().includes('mcq') || questionType.toLowerCase().includes('multiple choice');
    
    // Filter out generic topics and use meaningful ones from the document
    const validTopics = topics.filter(topic => 
      topic && 
      topic !== 'Based on uploaded content' && 
      topic !== 'General Knowledge' &&
      topic.length > 2 && 
      !topic.toLowerCase().includes('content') &&
      !topic.toLowerCase().includes('general')
    );
    
    if (validTopics.length === 0) {
      return this.generateContentBasedQuestion({ subject, topics }, index, questionType, difficulty);
    }
    
    const topic = validTopics[index % validTopics.length];
    
    if (isMultipleChoice) {
      // Create specific MCQ questions based on document topics
      const questionTemplates = [
        {
          question: `What is the key concept related to ${topic}?`,
          options: [
            `A) Core principles and applications of ${topic}`,
            `B) Superficial understanding of ${topic}`,
            `C) Avoiding ${topic} completely`,
            `D) Random approaches to ${topic}`
          ],
          correctAnswer: "A"
        },
        {
          question: `Which approach is most effective when studying ${topic}?`,
          options: [
            `A) Systematic analysis and practical application`,
            `B) Memorization without understanding`,
            `C) Ignoring fundamental concepts`,
            `D) Avoiding detailed study`
          ],
          correctAnswer: "A"
        },
        {
          question: `What is the significance of ${topic} in ${subject}?`,
          options: [
            `A) It provides essential understanding and practical knowledge`,
            `B) It has no practical relevance`,
            `C) It should be avoided in studies`,
            `D) It complicates the learning process`
          ],
          correctAnswer: "A"
        },
        {
          question: `How should one approach learning about ${topic}?`,
          options: [
            `A) Through comprehensive study and practical examples`,
            `B) By avoiding complex concepts`,
            `C) Through superficial reading only`,
            `D) By ignoring theoretical foundations`
          ],
          correctAnswer: "A"
        }
      ];
      
      return questionTemplates[index % questionTemplates.length];
    } else {
      // Create open-ended questions based on document topics
      const openQuestions = [
        `Explain the fundamental concepts of ${topic} and their practical applications in ${subject}.`,
        `Analyze the importance of ${topic} in the context of ${subject} and discuss its key components.`,
        `Describe the methodology used in ${topic} and evaluate its effectiveness.`,
        `Compare different approaches to understanding ${topic} and discuss their advantages.`,
        `Discuss the role of ${topic} in modern ${subject} and its future implications.`
      ];
      
      return {
        question: openQuestions[index % openQuestions.length]
      };
    }
  }

  private generateContentBasedQuestion(analysis: any, index: number, questionType: string, difficulty: string): any {
    const isMultipleChoice = questionType.toLowerCase().includes('mcq') || questionType.toLowerCase().includes('multiple choice');
    const subject = analysis.subject || 'the subject';
    
    if (isMultipleChoice) {
      // Create questions based on uploaded content context
      const contentQuestions = [
        {
          question: `Based on the uploaded content, what is the primary focus of this study material?`,
          options: [
            `A) Comprehensive understanding of key concepts and their applications`,
            `B) Superficial coverage of topics`,
            `C) Random collection of information`,
            `D) Outdated theoretical concepts only`
          ],
          correctAnswer: "A"
        },
        {
          question: `What learning approach is most suitable for this material?`,
          options: [
            `A) Active engagement with practical application`,
            `B) Passive reading without analysis`,
            `C) Memorization without understanding`,
            `D) Ignoring practical applications`
          ],
          correctAnswer: "A"
        },
        {
          question: `Which skill is essential for mastering the concepts in this content?`,
          options: [
            `A) Critical thinking and analytical reasoning`,
            `B) Memorization without understanding`,
            `C) Avoiding complex topics`,
            `D) Surface-level reading only`
          ],
          correctAnswer: "A"
        },
        {
          question: `What is the recommended strategy for studying this material effectively?`,
          options: [
            `A) Systematic study with practice and application`,
            `B) Quick reading without practice`,
            `C) Avoiding challenging sections`,
            `D) Relying on summaries only`
          ],
          correctAnswer: "A"
        }
      ];
      
      return contentQuestions[index % contentQuestions.length];
    } else {
      // Create content-based open-ended questions
      const openQuestions = [
        `Analyze the main themes presented in the uploaded content and discuss their significance.`,
        `Evaluate the methodologies discussed in the material and their practical applications.`,
        `Explain how the concepts in this content relate to real-world scenarios.`,
        `Discuss the key learning outcomes from this material and their importance.`,
        `Compare the different approaches mentioned in the content and assess their effectiveness.`
      ];
      
      return {
        question: openQuestions[index % openQuestions.length]
      };
    }
  }

  private getBloomLevel(difficulty: string, questionType: string): 'Remember' | 'Understand' | 'Apply' | 'Analyze' | 'Evaluate' | 'Create' {
    const lowerType = questionType.toLowerCase();
    
    if (lowerType.includes('define') || lowerType.includes('mcq')) {
      return 'Remember';
    } else if (lowerType.includes('explain') || lowerType.includes('short')) {
      return difficulty === 'easy' ? 'Understand' : 'Apply';
    } else if (lowerType.includes('analyze') || lowerType.includes('compare')) {
      return 'Analyze';
    } else if (lowerType.includes('evaluate') || lowerType.includes('assess')) {
      return 'Evaluate';
    } else if (lowerType.includes('design') || lowerType.includes('create')) {
      return 'Create';
    } else {
      // Default based on difficulty
      switch (difficulty) {
        case 'easy': return 'Remember';
        case 'medium': return 'Understand';
        case 'hard': return 'Apply';
        case 'expert': return 'Analyze';
        default: return 'Understand';
      }
    }
  }

  private buildEnhancedPrompt(analysisResult: AnalysisResult): string {
    const { analysis, prompt, paperPattern } = analysisResult;
    const parsedRequirements = analysis.parsedRequirements;
    
    // Create structured JSON payload for Bedrock agent
    const bedrockPayload = {
      task: "GENERATE_QUESTION_PAPER",
      analysis: analysis,
      requirements: parsedRequirements,
      paperPattern: paperPattern,
      extractedContent: {
        questions: analysis.extractedQuestions || [],
        patterns: analysis.questionPatterns || [],
        topics: analysis.topics,
        subject: analysis.subject,
        difficulty: analysis.difficulty
      },
      outputFormat: {
        type: "STRUCTURED_JSON",
        includeLatex: true,
        includeMermaidGraphs: true,
        includeVisualization: true,
        supportedElements: [
          "mathematical_equations",
          "integrals",
          "derivatives", 
          "graphs",
          "tables",
          "diagrams",
          "charts",
          "chemical_formulas",
          "physics_diagrams"
        ]
      }
    };

    let enhancedPrompt = `
GENERATE QUESTION PAPER - FOLLOW USER REQUIREMENTS EXACTLY

CRITICAL: You MUST follow the exact pattern and requirements specified by the user. Do NOT deviate from:
- Number of questions per section
- Marks per question
- Question types specified (MCQ, Short Answer, Long Answer, etc.)
- Total marks
- Duration
- Section structure

⚠️ SPECIAL ATTENTION FOR MCQ QUESTIONS:
If the user specifies MCQ or Multiple Choice questions, you MUST:
1. Generate ONLY MCQ questions (no Short Answer, Long Answer, or other types)
2. Include exactly 4 options (A, B, C, D) for each MCQ
3. Specify the correct answer
4. Make all questions multiple choice format

🚨 CRITICAL JSON OUTPUT REQUIREMENTS:
- OUTPUT ONLY VALID JSON - NO TEXT BEFORE OR AFTER THE JSON
- DO NOT include any explanatory text outside the JSON structure
- DO NOT mix text content with JSON
- DO NOT output partial JSON followed by text
- DO NOT include incomplete JSON objects
- Ensure all JSON brackets are properly closed
- Validate JSON structure before outputting
- If you cannot generate valid JSON, generate a simple text list instead

USER'S SPECIFIC REQUIREMENTS:
${parsedRequirements ? JSON.stringify(parsedRequirements, null, 2) : 'No specific requirements parsed'}

USER'S PAPER PATTERN:
${paperPattern ? JSON.stringify(paperPattern, null, 2) : 'No paper pattern provided'}

EXTRACTED QUESTIONS FROM USER'S MATERIAL (Use these as reference for style and complexity):
${analysis.extractedQuestions ? analysis.extractedQuestions.map((q, i) => `${i + 1}. ${q}`).join('\n') : 'No extracted questions available'}

IDENTIFIED QUESTION PATTERNS (Follow these patterns exactly):
${analysis.questionPatterns ? analysis.questionPatterns.map((p, i) => `${i + 1}. ${p}`).join('\n') : 'No specific patterns identified'}

MANDATORY RULES:
1. If user specifies "5 questions of 4 marks each" - generate EXACTLY 5 questions of 4 marks each
2. If user specifies section structure - follow the EXACT section structure
3. If user specifies question types - use ONLY those question types
4. If user specifies total marks - ensure total adds up to exactly that amount
5. If user specifies duration - use that exact duration
6. Maintain the same difficulty level and style as extracted questions
7. Use the same subject terminology and concepts from user's material

RESPONSE FORMAT:
Generate a JSON response with this EXACT structure:
{
  "title": "Use user's title or create based on subject",
  "subject": "Use exact subject from analysis",
  "duration": "Use exact duration from requirements",
  "totalMarks": "Use exact total marks from requirements",
  "timestamp": "Current ISO timestamp",
  "generatedBy": "AWS Bedrock Agent",
  "instructions": ["Follow user's instructions if provided, otherwise use standard"],
  "sections": [
    {
      "sectionName": "Use exact section names from user requirements",
      "instructions": "Follow user's section instructions",
      "totalMarks": "Calculate based on user's requirements",
      "questions": [
        {
          "questionNumber": 1,
          "question": "Generate based on user's content and style",
          "marks": "Use exact marks per question as specified by user",
          "questionType": "Use exact question type specified by user",
          "options": ["Only if MCQ"],
          "correctAnswer": "Only if MCQ",
          "answerKey": "Detailed solution",
          "bloomLevel": "Appropriate level"
        }
      ]
    }
  ]
}

SUBJECT: ${analysis.subject}
DIFFICULTY: ${analysis.difficulty}
TOPICS: ${analysis.topics.join(', ')}

PAYLOAD FOR PROCESSING:
${JSON.stringify(bedrockPayload, null, 2)}

Generate the question paper following the user's EXACT specifications. Do not add extra questions or change the structure.
${paperPattern ? JSON.stringify(paperPattern, null, 2) : 'Use standard academic format'}

OUTPUT REQUIREMENTS:
Generate a complete question paper as a JSON object with this EXACT structure:

{
  "questionPaper": {
    "title": "Complete Paper Title",
    "subject": "${analysis.subject}",
    "duration": "${paperPattern?.examDuration || '3 hours'}",
    "totalMarks": ${paperPattern?.totalMarks ? parseInt(paperPattern.totalMarks) : 100},
    "instructions": [
      "General instructions array"
    ],
    "sections": [
      {
        "sectionName": "Section A",
        "instructions": "Section specific instructions",
        "totalMarks": 25,
        "questions": [
          {
            "questionNumber": 1,
            "question": "Question text with mathematical content",
            "marks": 5,
            "questionType": "Use EXACT question type from user pattern (MCQ|Short Answer|Long Answer|Numerical|Essay)",
            "options": ["Required for MCQ: Option A", "Option B", "Option C", "Option D"],
            "correctAnswer": "Required for MCQ: The correct option",
            },
            "options": ["Option A", "Option B", "Option C", "Option D"],
            "correctAnswer": "Option A",
            "answerKey": "Detailed solution with steps",
            "bloomLevel": "Apply"
          }
        ]
      }
    ],
    "answerKey": {
      "solutions": [
        {
          "questionNumber": 1,
          "solution": "Step-by-step solution with mathematical workings",
          "markingScheme": "1 mark for setup, 2 marks for calculation, 2 marks for final answer"
        }
      ]
    },
    "metadata": {
      "generatedBy": "AI Agent",
      "timestamp": "${new Date().toISOString()}",
      "difficulty": "${analysis.difficulty}",
      "estimatedTime": "${paperPattern?.examDuration || '3 hours'}"
    }
  }
}

MATHEMATICAL RENDERING GUIDELINES:
- Use LaTeX syntax: \\\\frac{a}{b}, \\\\int, \\\\sum, \\\\sqrt{}, \\\\alpha, \\\\beta
- Integrals: \\\\int_{a}^{b} f(x)dx
- Derivatives: \\\\frac{d}{dx}, \\\\frac{\\\\partial}{\\\\partial x}
- Matrices: \\\\begin{pmatrix} a & b \\\\\\\\ c & d \\\\end{pmatrix}
- Chemical: H_2SO_4, CO_2, NaOH

MERMAID GRAPH EXAMPLES:
- Flowchart: flowchart TD\\\\nA[Start] --> B{Decision}\\\\nB -->|Yes| C[Process]\\\\nB -->|No| D[End]
- Graph: graph LR\\\\nA --> B --> C
- Sequence: sequenceDiagram\\\\nAlice->>Bob: Hello

ENSURE FOR MCQ QUESTIONS:
1. Every MCQ question MUST have exactly 4 options (A, B, C, D)
2. Every MCQ question MUST have a correctAnswer field
3. Question text should be in MCQ format ("Which of the following...")
4. NO mixing of question types if user specified MCQ only
5. All mathematical content is properly formatted with LaTeX
6. All graphs/diagrams include Mermaid code
7. Tables are well-structured
8. Questions match the extracted patterns exactly
9. Proper JSON structure without syntax errors
10. Complete answer keys with detailed solutions
11. Appropriate difficulty progression
12. Cover all specified topics comprehensively

Generate the most accurate, examination-worthy question paper that closely matches real academic standards and the provided reference materials.
    `;

    return enhancedPrompt.trim();
  }

  private fixTruncatedJSON(jsonString: string): string {
    // Remove any trailing commas before closing brackets/braces
    jsonString = jsonString.replace(/,(\s*[}\]])/g, '$1');
    
    // Count open and close braces/brackets
    const openBraces = (jsonString.match(/{/g) || []).length;
    const closeBraces = (jsonString.match(/}/g) || []).length;
    const openBrackets = (jsonString.match(/\[/g) || []).length;
    const closeBrackets = (jsonString.match(/\]/g) || []).length;
    
    console.log(`📄 JSON structure check: {${openBraces}/${closeBraces}} [${openBrackets}/${closeBrackets}]`);
    
    // If JSON is truncated, try to close it properly
    if (openBraces > closeBraces || openBrackets > closeBrackets) {
      console.log('📄 Detected truncated JSON, attempting to fix...');
      
      let fixed = jsonString.trim();
      
      // Remove any incomplete trailing content
      // Look for incomplete structures at the end
      const lines = fixed.split('\n');
      let lastCompleteLineIndex = lines.length - 1;
      
      // Work backwards to find the last complete line
      for (let i = lines.length - 1; i >= 0; i--) {
        const line = lines[i].trim();
        // If line ends with comma, colon, complete string, or closing bracket/brace - it's likely complete
        if (line.endsWith(',') || line.endsWith(':') || line.endsWith('"') || 
            line.endsWith('}') || line.endsWith(']') || line === '') {
          lastCompleteLineIndex = i;
          break;
        }
      }
      
      // Reconstruct from complete lines
      if (lastCompleteLineIndex < lines.length - 1) {
        fixed = lines.slice(0, lastCompleteLineIndex + 1).join('\n');
        console.log('📄 Removed incomplete trailing content');
      }
      
      // Remove trailing comma if present
      fixed = fixed.replace(/,\s*$/, '');
      
      // Add missing closing brackets
      const stillOpenBrackets = (fixed.match(/\[/g) || []).length - (fixed.match(/\]/g) || []).length;
      for (let i = 0; i < stillOpenBrackets; i++) {
        fixed += ']';
      }
      
      // Add missing closing braces
      const stillOpenBraces = (fixed.match(/{/g) || []).length - (fixed.match(/}/g) || []).length;
      for (let i = 0; i < stillOpenBraces; i++) {
        fixed += '}';
      }
      
      console.log('📄 Fixed JSON structure');
      return fixed;
    }
    
    return jsonString;
  }

  private parseQuestionPaper(
    generatedContent: string, 
    analysisResult: AnalysisResult, 
    selectedAgent: BedrockAgent
  ): QuestionPaper {
    try {
      console.log('🔍 Raw Bedrock Response:', generatedContent.substring(0, 500), '...');
      
      // Try to parse the JSON response from Bedrock
      let parsedResponse;
      try {
        // Multiple strategies to extract JSON
        let jsonString = generatedContent;
        
        // Strategy 1: Look for complete JSON object
        let jsonMatch = generatedContent.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          jsonString = jsonMatch[0];
          console.log('📦 Found JSON match:', jsonString.substring(0, 200), '...');
        }
        
        // Strategy 2: Look for questionPaper object specifically
        let questionPaperMatch = null;
        if (!jsonMatch) {
          questionPaperMatch = generatedContent.match(/"questionPaper"\s*:\s*\{[\s\S]*?\}\s*\}/);
          if (questionPaperMatch) {
            jsonString = `{${questionPaperMatch[0]}}`;
            console.log('📋 Found questionPaper match');
          }
        }
        
        // Strategy 3: Clean up mixed content by finding JSON boundaries
        if (!jsonMatch && !questionPaperMatch) {
          // Look for JSON-like structures and clean up text content
          const lines = generatedContent.split('\n');
          let jsonStartIndex = -1;
          let jsonEndIndex = -1;
          let braceCount = 0;
          
          for (let i = 0; i < lines.length; i++) {
            const line = lines[i].trim();
            if (line.includes('{') && jsonStartIndex === -1) {
              jsonStartIndex = i;
            }
            if (jsonStartIndex !== -1) {
              braceCount += (line.match(/{/g) || []).length;
              braceCount -= (line.match(/}/g) || []).length;
              if (braceCount === 0 && jsonStartIndex !== -1) {
                jsonEndIndex = i;
                break;
              }
            }
          }
          
          if (jsonStartIndex !== -1 && jsonEndIndex !== -1) {
            jsonString = lines.slice(jsonStartIndex, jsonEndIndex + 1).join('\n');
            console.log('🔧 Extracted JSON from mixed content');
          } else {
            console.log('🔄 No valid JSON found, using clean fallback generation...');
            return this.createFallbackQuestionPaperFromAnalysis(analysisResult);
          }
        }
        
        // Fix common JSON issues before parsing
        jsonString = this.fixTruncatedJSON(jsonString);
        
        parsedResponse = JSON.parse(jsonString);
        console.log('✅ Successfully parsed JSON response');
        
      } catch (parseError) {
        console.error('❌ JSON Parse Error:', parseError);
        console.log('🔄 Using clean fallback generation instead of text parsing...');
        return this.createFallbackQuestionPaperFromAnalysis(analysisResult);
      }

      // Extract the question paper from the parsed response
      const questionPaperData = parsedResponse.questionPaper || parsedResponse;
      
      return {
        title: questionPaperData.title || `${analysisResult.analysis.subject} Question Paper`,
        subject: questionPaperData.subject || analysisResult.analysis.subject,
        duration: questionPaperData.duration || analysisResult.paperPattern?.examDuration || '3 hours',
        totalMarks: questionPaperData.totalMarks || parseInt(analysisResult.paperPattern?.totalMarks || '100'),
        sections: this.parseSections(questionPaperData.sections || []),
        instructions: questionPaperData.instructions || this.extractInstructions(generatedContent),
        generatedBy: selectedAgent.name,
        timestamp: new Date().toISOString(),
        answerKey: questionPaperData.answerKey,
        metadata: questionPaperData.metadata || {
          generatedBy: selectedAgent.name,
          timestamp: new Date().toISOString(),
          difficulty: analysisResult.analysis.difficulty,
          estimatedTime: analysisResult.paperPattern?.examDuration || '3 hours'
        }
      };
    } catch (error) {
      console.error('Error parsing question paper:', error);
      console.log('🔄 Using clean fallback generation due to parsing error...');
      return this.createFallbackQuestionPaperFromAnalysis(analysisResult);
    }
  }

  private parseSections(sectionsData: any[]): QuestionSection[] {
    return sectionsData.map(section => ({
      sectionName: section.sectionName || 'Section',
      instructions: section.instructions || 'Answer all questions in this section',
      questions: this.parseQuestions(section.questions || []),
      totalMarks: section.totalMarks || section.questions?.reduce((sum: number, q: any) => sum + (q.marks || 0), 0) || 0
    }));
  }

  private parseQuestions(questionsData: any[]): Question[] {
    return questionsData.map(q => {
      const question: Question = {
        questionNumber: q.questionNumber || 1,
        question: q.question || 'Question text',
        marks: q.marks || 1,
        questionType: q.questionType || 'Short Answer',
        options: q.options,
        correctAnswer: q.correctAnswer,
        answerKey: q.answerKey,
        bloomLevel: q.bloomLevel,
        mathContent: q.mathContent,
        visualContent: q.visualContent
      };
      
      // Validate and fix MCQ questions
      if (question.questionType === 'MCQ' || question.questionType.toLowerCase().includes('mcq') || question.questionType.toLowerCase().includes('multiple choice')) {
        // Ensure question type is exactly 'MCQ'
        question.questionType = 'MCQ';
        
        if (!question.options || question.options.length !== 4) {
          console.warn('⚠️ MCQ question missing options, adding default options');
          question.options = [
            'Option A - First possible answer',
            'Option B - Second possible answer',
            'Option C - Third possible answer',
            'Option D - Fourth possible answer'
          ];
        }
        if (!question.correctAnswer) {
          console.warn('⚠️ MCQ question missing correct answer, setting default');
          question.correctAnswer = question.options[0];
        }
      }
      
      return question;
    });
  }

  private parseTextResponseToQuestionPaper(
    content: string,
    analysisResult: AnalysisResult,
    selectedAgent: BedrockAgent
  ): QuestionPaper {
    console.log('� Parsing text response to question paper...');
    console.log('📄 Content preview:', content.substring(0, 500) + '...');
    
    const title = `${analysisResult.analysis.subject} Question Paper - ${selectedAgent.name}`;
    const duration = analysisResult.paperPattern?.examDuration || '3 hours';
    const totalMarks = parseInt(analysisResult.paperPattern?.totalMarks || '100');
    
    // Get user's specified question type from paper pattern
    const userSpecifiedQuestionType = analysisResult.paperPattern?.sections?.[0]?.questionType || 
                                      analysisResult.analysis.parsedRequirements?.sections?.[0]?.questionType || 
                                      'Short Answer';
    console.log('🎯 Using user-specified question type:', userSpecifiedQuestionType);
    
    const sections: QuestionSection[] = [];
    let currentSection: QuestionSection | null = null;
    let questionCounter = 1;
    
    // Split content into lines and clean them
    const cleanedContent = this.cleanMixedContent(content);
    const lines = cleanedContent.split('\n').map(line => line.trim()).filter(line => line.length > 0);
    
    // Enhanced patterns to identify different elements
    const sectionPattern = /^(Section\s+[A-Z]|Part\s+[A-Z]|Chapter\s+\d+|Unit\s+\d+|Section\s+\d+)(?:\s*[-:]?\s*(.*))?/i;
    const questionPatterns = [
      /^(\d+)[\.\)]\s*(.+)/,                    // 1. Question text
      /^Q[\.\s]*(\d+)[\.\):]?\s*(.+)/i,         // Q1. Question text
      /^Question\s*(\d+)[\.\):]?\s*(.+)/i,      // Question 1: text
      /^\[(\d+)\]\s*(.+)/,                      // [1] Question text
      /^(\d+)\s*[-–]\s*(.+)/                    // 1 - Question text
    ];
    
    // Question content patterns (for unnumbered questions)
    const questionContentPatterns = [
      /what\s+(?:is|are|do|does|can|will|would|should)/i,
      /how\s+(?:do|does|can|will|would|should|to)/i,
      /why\s+(?:is|are|do|does|did|can|will|would|should)/i,
      /when\s+(?:is|are|do|does|did|can|will|would|should)/i,
      /where\s+(?:is|are|do|does|did|can|will|would|should)/i,
      /which\s+(?:is|are|do|does|did|can|will|would|should)/i,
      /explain\s+(?:the|how|why|what|when|where)/i,
      /describe\s+(?:the|how|why|what|when|where)/i,
      /define\s+(?:the|what)/i,
      /calculate\s+(?:the|what)/i,
      /solve\s+(?:the|for)/i,
      /find\s+(?:the|what)/i,
      /derive\s+(?:the|an|a)/i,
      /prove\s+(?:that|the)/i,
      /discuss\s+(?:the|how|why)/i,
      /analyze\s+(?:the|how|why)/i,
      /compare\s+(?:and|the)/i,
      /contrast\s+(?:the|with)/i,
      /evaluate\s+(?:the|how)/i,
      /\?$/                                     // Ends with question mark
    ];
    
    console.log(`📊 Processing ${lines.length} lines of content...`);
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmedLine = line.trim();
      
      // Skip empty lines or very short lines
      if (trimmedLine.length < 5) continue;
      
      // Check for section headers
      const sectionMatch = trimmedLine.match(sectionPattern);
      if (sectionMatch) {
        console.log(`📑 Found section: ${trimmedLine}`);
        // Save previous section if it exists
        if (currentSection && currentSection.questions.length > 0) {
          sections.push(currentSection);
        }
        currentSection = {
          sectionName: trimmedLine,
          instructions: '',
          questions: [],
          totalMarks: 0
        };
        continue;
      }
      
      // Check for numbered questions using multiple patterns
      let questionFound = false;
      for (const pattern of questionPatterns) {
        const questionMatch = trimmedLine.match(pattern);
        if (questionMatch && questionMatch[2] && questionMatch[2].trim().length > 10) {
          console.log(`❓ Found numbered question: ${questionMatch[1]} - ${questionMatch[2].substring(0, 50)}...`);
          
          if (!currentSection) {
            currentSection = {
              sectionName: 'Section A',
              instructions: `Answer all questions. Each question carries ${analysisResult.analysis.parsedRequirements?.marksPerQuestion || 4} marks.`,
              questions: [],
              totalMarks: 0
            };
          }
          
          // Check if next lines are part of this question (multi-line questions)
          let fullQuestion = questionMatch[2].trim();
          let nextLineIndex = i + 1;
          while (nextLineIndex < lines.length) {
            const nextLine = lines[nextLineIndex].trim();
            // If next line looks like a new question or section, stop
            if (nextLine.match(/^\d+[\.\)]/)) break;
            if (nextLine.match(sectionPattern)) break;
            if (nextLine.length < 5) break;
            
            // If it looks like continuation of the question
            if (nextLine.length > 10 && !nextLine.match(/^[A-Z][a-z]*\s*:/) && !nextLine.includes('marks')) {
              fullQuestion += ' ' + nextLine;
              i = nextLineIndex; // Skip this line in main loop
              nextLineIndex++;
            } else {
              break;
            }
          }
          
          const question: Question = {
            questionNumber: parseInt(questionMatch[1]) || questionCounter++,
            question: fullQuestion,
            marks: this.extractMarksFromQuestion(fullQuestion) || (analysisResult.analysis.parsedRequirements?.marksPerQuestion || 4),
            questionType: userSpecifiedQuestionType as Question['questionType']
          };
          
          // Add MCQ options if the question type is MCQ
          if (userSpecifiedQuestionType.toLowerCase().includes('mcq') || userSpecifiedQuestionType.toLowerCase().includes('multiple choice')) {
            question.options = [
              'Option A - First possible answer',
              'Option B - Second possible answer',
              'Option C - Third possible answer',
              'Option D - Fourth possible answer'
            ];
            question.correctAnswer = 'Option A';
          }
          
          currentSection.questions.push(question);
          currentSection.totalMarks += question.marks;
          questionFound = true;
          break;
        }
      }
      
      if (questionFound) continue;
      
      // Check for unnumbered questions using content patterns
      const isQuestionContent = questionContentPatterns.some(pattern => 
        trimmedLine.match(pattern)
      );
      
      if (isQuestionContent && trimmedLine.length > 15) {
        console.log(`❓ Found unnumbered question: ${trimmedLine.substring(0, 50)}...`);
        
        if (!currentSection) {
          currentSection = {
            sectionName: 'General Questions',
            instructions: `Answer all questions. Each question carries ${analysisResult.analysis.parsedRequirements?.marksPerQuestion || 4} marks.`,
            questions: [],
            totalMarks: 0
          };
        }
        
        const question: Question = {
          questionNumber: questionCounter++,
          question: trimmedLine,
          marks: this.extractMarksFromQuestion(trimmedLine) || (analysisResult.analysis.parsedRequirements?.marksPerQuestion || 4),
          questionType: userSpecifiedQuestionType as Question['questionType']
        };
        
        // Add MCQ options if the question type is MCQ
        if (userSpecifiedQuestionType.toLowerCase().includes('mcq') || userSpecifiedQuestionType.toLowerCase().includes('multiple choice')) {
          question.options = [
            'Option A - First possible answer',
            'Option B - Second possible answer',
            'Option C - Third possible answer',
            'Option D - Fourth possible answer'
          ];
          question.correctAnswer = 'Option A';
        }
        
        currentSection.questions.push(question);
        currentSection.totalMarks += question.marks;
      }
    }
    
    // Add the last section if it exists
    if (currentSection && currentSection.questions.length > 0) {
      sections.push(currentSection);
    }
    
    // Check if we have enough questions based on user requirements
    const expectedQuestions = analysisResult.analysis.parsedRequirements?.totalQuestions || 5;
    const expectedMarksPerQuestion = analysisResult.analysis.parsedRequirements?.marksPerQuestion || 4;
    let totalQuestionsFound = sections.reduce((sum, s) => sum + s.questions.length, 0);
    
    if (totalQuestionsFound < expectedQuestions) {
      console.log(`⚠️ Found only ${totalQuestionsFound} questions, need ${expectedQuestions}. Extracting more...`);
      
      // Split content into paragraphs and look for question-like sentences
      const paragraphs = content.split(/\n\s*\n/).filter(p => p.trim().length > 20);
      const additionalQuestions: Question[] = [];
      
      for (const paragraph of paragraphs) {
        if (additionalQuestions.length >= (expectedQuestions - totalQuestionsFound)) break;
        
        const sentences = paragraph.split(/[.!]/).filter(s => s.trim().length > 15);
        
        for (const sentence of sentences) {
          if (additionalQuestions.length >= (expectedQuestions - totalQuestionsFound)) break;
          
          const trimmedSentence = sentence.trim();
          if (questionContentPatterns.some(pattern => trimmedSentence.match(pattern))) {
            const additionalQuestion: Question = {
              questionNumber: totalQuestionsFound + additionalQuestions.length + 1,
              question: trimmedSentence.endsWith('?') ? trimmedSentence : trimmedSentence + '?',
              marks: expectedMarksPerQuestion,
              questionType: userSpecifiedQuestionType as Question['questionType']
            };
            
            // Add MCQ options if the question type is MCQ
            if (userSpecifiedQuestionType.toLowerCase().includes('mcq') || userSpecifiedQuestionType.toLowerCase().includes('multiple choice')) {
              additionalQuestion.options = [
                'Option A - First possible answer',
                'Option B - Second possible answer',
                'Option C - Third possible answer',
                'Option D - Fourth possible answer'
              ];
              additionalQuestion.correctAnswer = 'Option A';
            }
            
            additionalQuestions.push(additionalQuestion);
          }
        }
      }
      
      // If we still need more questions, create them from the topics
      if (additionalQuestions.length < (expectedQuestions - totalQuestionsFound)) {
        const topics = analysisResult.analysis.topics || ['General Knowledge'];
        const remainingQuestions = expectedQuestions - totalQuestionsFound - additionalQuestions.length;
        
        // Filter out generic topics and create better ones
        const validTopics = topics.filter(topic => 
          topic && 
          topic !== 'Based on uploaded content' && 
          topic.length > 2 && 
          !topic.includes('content')
        );
        
        const fallbackTopics = validTopics.length > 0 ? validTopics : [
          'Mathematics', 'Science', 'History', 'Geography', 'Literature', 
          'Physics', 'Chemistry', 'Biology', 'Economics', 'Technology'
        ];
        
        // Better question templates for MCQs
        const mcqTemplates = [
          {
            question: (topic: string) => `What is the primary focus of ${topic}?`,
            options: (topic: string) => [
              `The study and application of ${topic} principles`,
              `The rejection of ${topic} concepts`,
              `The historical criticism of ${topic}`,
              `The elimination of ${topic} studies`
            ]
          },
          {
            question: (topic: string) => `Which statement best describes ${topic}?`,
            options: (topic: string) => [
              `${topic} is a fundamental academic discipline`,
              `${topic} has no practical applications`,
              `${topic} is only for entertainment`,
              `${topic} should be avoided in education`
            ]
          },
          {
            question: (topic: string) => `In which field would ${topic} be most relevant?`,
            options: (topic: string) => [
              `Academic and professional settings`,
              `Only in historical contexts`,
              `Exclusively for research purposes`,
              `Not applicable to any field`
            ]
          },
          {
            question: (topic: string) => `What role does ${topic} play in modern education?`,
            options: (topic: string) => [
              `It provides essential knowledge and skills`,
              `It has no place in current curricula`,
              `It is only useful for specialists`,
              `It should be replaced entirely`
            ]
          }
        ];
        
        for (let i = 0; i < remainingQuestions; i++) {
          const topic = fallbackTopics[i % fallbackTopics.length];
          const template = mcqTemplates[i % mcqTemplates.length];
          
          const topicQuestion: Question = {
            questionNumber: totalQuestionsFound + additionalQuestions.length + 1,
            question: userSpecifiedQuestionType.toLowerCase().includes('mcq') ? 
              template.question(topic) : 
              `Explain the concept of ${topic} with suitable examples.`,
            marks: expectedMarksPerQuestion,
            questionType: userSpecifiedQuestionType as Question['questionType']
          };
          
          // Add MCQ options if the question type is MCQ
          if (userSpecifiedQuestionType.toLowerCase().includes('mcq') || userSpecifiedQuestionType.toLowerCase().includes('multiple choice')) {
            const options = template.options(topic);
            topicQuestion.options = options.map((opt, idx) => `${String.fromCharCode(65 + idx)}) ${opt}`);
            topicQuestion.correctAnswer = 'A';
          }
          
          additionalQuestions.push(topicQuestion);
        }
      }
      
      if (additionalQuestions.length > 0) {
        console.log(`✅ Added ${additionalQuestions.length} additional questions to meet requirements`);
        const existingSection = sections.find(s => s.sectionName.includes('General') || s.sectionName.includes('Section A'));
        if (existingSection) {
          existingSection.questions.push(...additionalQuestions);
          existingSection.totalMarks += additionalQuestions.reduce((sum, q) => sum + q.marks, 0);
        } else {
          sections.push({
            sectionName: 'Additional Questions',
            instructions: `Answer all questions. Each question carries ${expectedMarksPerQuestion} marks.`,
            questions: additionalQuestions,
            totalMarks: additionalQuestions.reduce((sum, q) => sum + q.marks, 0)
          });
        }
      }
      
      // Update total count
      totalQuestionsFound = sections.reduce((sum, s) => sum + s.questions.length, 0);
    }
    
    // Enhanced fallback: if no clear questions found, try to extract from paragraphs
    if (sections.length === 0 || sections.every(s => s.questions.length === 0)) {
      console.log('⚠️ No clear questions found, trying paragraph extraction...');
      
      // Split content into paragraphs and look for question-like sentences
      const paragraphs = content.split(/\n\s*\n/).filter(p => p.trim().length > 20);
      const extractedQuestions: Question[] = [];
      
      for (const paragraph of paragraphs) {
        const sentences = paragraph.split(/[.!]/).filter(s => s.trim().length > 15);
        
        for (const sentence of sentences) {
          const trimmedSentence = sentence.trim();
          if (questionContentPatterns.some(pattern => trimmedSentence.match(pattern))) {
            const extractedQuestion: Question = {
              questionNumber: extractedQuestions.length + 1,
              question: trimmedSentence.endsWith('?') ? trimmedSentence : trimmedSentence + '?',
              marks: analysisResult.analysis.parsedRequirements?.marksPerQuestion || 4,
              questionType: userSpecifiedQuestionType as Question['questionType']
            };
            
            // Add MCQ options if the question type is MCQ
            if (userSpecifiedQuestionType.toLowerCase().includes('mcq') || userSpecifiedQuestionType.toLowerCase().includes('multiple choice')) {
              extractedQuestion.options = [
                'Option A - First possible answer',
                'Option B - Second possible answer',
                'Option C - Third possible answer',
                'Option D - Fourth possible answer'
              ];
              extractedQuestion.correctAnswer = 'Option A';
            }
            
            extractedQuestions.push(extractedQuestion);
          }
        }
      }
      
      if (extractedQuestions.length > 0) {
        console.log(`✅ Extracted ${extractedQuestions.length} questions from paragraphs`);
        sections.push({
          sectionName: 'Extracted Questions',
          instructions: 'Answer all questions based on the provided content.',
          questions: extractedQuestions,
          totalMarks: extractedQuestions.reduce((sum, q) => sum + q.marks, 0)
        });
      } else {
        // Last resort: create questions from the raw content
        console.log('⚠️ Creating fallback questions from content...');
        const contentLines = content.split('\n').filter(line => line.trim().length > 30);
        const fallbackQuestions: Question[] = [];
        
        contentLines.slice(0, 5).forEach((line, index) => {
          if (line.trim().length > 20) {
            const fallbackQuestion: Question = {
              questionNumber: index + 1,
              question: userSpecifiedQuestionType.toLowerCase().includes('mcq') ?
                `Which of the following best describes: "${line.trim().substring(0, 80)}${line.trim().length > 80 ? '...' : ''}"?` :
                `Explain the following concept: "${line.trim().substring(0, 100)}${line.trim().length > 100 ? '...' : ''}"`,
              marks: analysisResult.analysis.parsedRequirements?.marksPerQuestion || 4,
              questionType: userSpecifiedQuestionType as Question['questionType']
            };
            
            // Add MCQ options if the question type is MCQ
            if (userSpecifiedQuestionType.toLowerCase().includes('mcq') || userSpecifiedQuestionType.toLowerCase().includes('multiple choice')) {
              fallbackQuestion.options = [
                'It is a fundamental concept',
                'It is not relevant',
                'It is an advanced topic',
                'It is a basic principle'
              ];
              fallbackQuestion.correctAnswer = fallbackQuestion.options[0];
            }
            
            fallbackQuestions.push(fallbackQuestion);
          }
        });
        
        sections.push({
          sectionName: 'Content-Based Questions',
          instructions: 'Answer all questions based on your understanding of the provided material.',
          questions: fallbackQuestions,
          totalMarks: fallbackQuestions.reduce((sum, q) => sum + q.marks, 0)
        });
      }
    }
    
    const finalQuestionCount = sections.reduce((sum, s) => sum + s.questions.length, 0);
    console.log(`✅ Parsed ${sections.length} sections with ${finalQuestionCount} questions from text`);
    
    return {
      title,
      subject: analysisResult.analysis.subject,
      duration,
      totalMarks: sections.reduce((sum, s) => sum + s.totalMarks, 0) || (analysisResult.analysis.parsedRequirements?.totalMarks || (finalQuestionCount * (analysisResult.analysis.parsedRequirements?.marksPerQuestion || 4))),
      sections,
      instructions: [
        'Read all questions carefully before attempting.',
        'Answer all questions unless otherwise specified.',
        'Write clearly and manage your time effectively.',
        'Show all working where applicable.'
      ],
      generatedBy: selectedAgent.name,
      timestamp: new Date().toISOString(),
      metadata: {
        generatedBy: selectedAgent.name,
        timestamp: new Date().toISOString(),
        difficulty: analysisResult.analysis.difficulty,
        estimatedTime: duration
      }
    };
  }
  
  private extractMarksFromQuestion(questionText: string): number {
    const marksPattern = /\[(\d+)\s*marks?\]|\((\d+)\s*marks?\)|(\d+)\s*marks?/i;
    const match = questionText.match(marksPattern);
    if (match) {
      return parseInt(match[1] || match[2] || match[3]);
    }
    
    // Determine marks based on question type/length
    if (questionText.length > 200) return 20;
    if (questionText.length > 100) return 15;
    return 10;
  }
  
  private determineQuestionTypeFromContent(questionText: string): Question['questionType'] {
    const text = questionText.toLowerCase();
    
    if (text.includes('multiple choice') || text.includes('choose') || text.includes('select')) {
      return 'MCQ';
    } else if (text.includes('calculate') || text.includes('solve') || text.includes('find the value')) {
      return 'Numerical';
    } else if (text.includes('explain in detail') || text.includes('discuss') || text.includes('analyze') || text.length > 150) {
      return 'Long Answer';
    } else if (text.includes('define') || text.includes('what is') || text.includes('list')) {
      return 'Short Answer';
    }
    
    return 'Short Answer';
  }

  private createFallbackQuestionPaperFromContent(
    content: string, 
    analysisResult: AnalysisResult, 
    selectedAgent: BedrockAgent
  ): QuestionPaper {
    // Create a basic question paper structure if parsing fails
    const lines = content.split('\n').filter(line => line.trim());
    
    return {
      title: `${analysisResult.analysis.subject} Question Paper`,
      subject: analysisResult.analysis.subject,
      duration: analysisResult.paperPattern?.examDuration || '3 hours',
      totalMarks: parseInt(analysisResult.paperPattern?.totalMarks || '100'),
      sections: this.extractSections(content, analysisResult),
      instructions: this.extractInstructions(content),
      generatedBy: selectedAgent.name,
      timestamp: new Date().toISOString()
    };
  }

  private extractSections(content: string, analysisResult: AnalysisResult): QuestionSection[] {
    // This is a simplified extraction - enhance based on actual response format
    const sections: QuestionSection[] = [];
    
    if (analysisResult.paperPattern?.sections) {
      analysisResult.paperPattern.sections.forEach((section, index) => {
        const questions: Question[] = [];
        
        // Generate sample questions based on the section
        for (let i = 1; i <= section.questions; i++) {
          questions.push({
            questionNumber: i,
            question: `Question ${i} for ${section.sectionName}`,
            marks: section.marksPerQuestion,
            questionType: this.determineQuestionType(section.sectionName),
          });
        }
        
        sections.push({
          sectionName: section.sectionName,
          instructions: section.instructions,
          questions,
          totalMarks: section.totalMarks
        });
      });
    }
    
    return sections;
  }

  private extractInstructions(content: string): string[] {
    // Extract general instructions from the generated content
    const instructions = [
      'Read all instructions carefully before attempting the questions.',
      'All questions are compulsory unless otherwise specified.',
      'Write clearly and legibly.',
      'Manage your time effectively.'
    ];
    
    return instructions;
  }

  private determineQuestionType(sectionName: string): Question['questionType'] {
    const sectionLower = sectionName.toLowerCase();
    
    if (sectionLower.includes('multiple choice') || sectionLower.includes('mcq')) {
      return 'MCQ';
    } else if (sectionLower.includes('short')) {
      return 'Short Answer';
    } else if (sectionLower.includes('long') || sectionLower.includes('essay')) {
      return 'Long Answer';
    } else if (sectionLower.includes('numerical')) {
      return 'Numerical';
    }
    
    return 'Short Answer';
  }

  getAvailableAgents(): BedrockAgent[] {
    return this.agents;
  }

  // Debug method to check service configuration
  getDebugInfo(): any {
    return {
      isBedrockAvailable: this.isBedrockAvailable,
      hasClient: !!this.client,
      agentCount: this.agents.length,
      agents: this.agents.map(agent => ({
        name: agent.name,
        id: agent.id,
        alias: agent.alias,
        subjects: agent.subjects
      })),
      environment: {
        accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID ? 'SET' : 'MISSING',
        secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY ? 'SET' : 'MISSING',
        region: process.env.REACT_APP_AWS_REGION || 'eu-north-1',
        agentAlias: process.env.REACT_APP_BEDROCK_AGENT_ALIAS || 'MISSING'
      }
    };
  }

  // Flashcard generation method using Bedrock agent
  async generateFlashCards(
    analysisResult: AnalysisResult,
    agentId?: string
  ): Promise<BedrockFlashCardSet> {
    console.log('🎴 Attempting to generate flashcards with AWS Bedrock...');
    
    try {
      // Select appropriate agent for flashcard generation
      let selectedAgent: BedrockAgent;
      
      if (agentId) {
        // Use specific agent ID provided by user
        const customAgent = this.agents.find(agent => agent.id === agentId);
        if (!customAgent) {
          throw new Error(`Agent with ID ${agentId} not found`);
        }
        selectedAgent = customAgent;
      } else {
        // Auto-select best agent based on subject
        selectedAgent = this.selectBestAgent(analysisResult);
      }

      console.log('🤖 Selected agent for flashcards:', selectedAgent.name, 'ID:', selectedAgent.id);

      if (!this.client) {
        throw new Error('Bedrock client is not initialized');
      }

      // Build the enhanced prompt for flashcard generation
      const flashCardPrompt = this.buildFlashCardPrompt(analysisResult);
      
      const command = new InvokeAgentCommand({
        agentId: selectedAgent.id.trim(),
        agentAliasId: selectedAgent.alias.trim(),
        sessionId: `flashcard-session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        inputText: flashCardPrompt,
      });

      console.log('🎴 Invoking Bedrock agent for flashcard generation...');
      console.log('📝 Prompt length:', flashCardPrompt.length);

      const response = await this.client.send(command);
      
      // Process the response
      let generatedContent = '';
      if (response.completion) {
        for await (const chunk of response.completion) {
          if (chunk.chunk?.bytes) {
            const text = new TextDecoder().decode(chunk.chunk.bytes);
            generatedContent += text;
          }
        }
      }

      console.log('📄 Generated flashcard content length:', generatedContent.length);
      
      if (!generatedContent || generatedContent.trim().length === 0) {
        console.warn('⚠️ No flashcard content generated by Bedrock agent, using fallback');
        return this.createFallbackFlashCardSet(analysisResult);
      }

      // Parse the generated content into structured flashcards
      const flashCardSet = this.parseFlashCardSet(generatedContent, analysisResult, selectedAgent);
      
      console.log('✅ Successfully generated flashcard set with Bedrock');
      console.log(`🎴 Generated ${flashCardSet.totalCards} flashcards`);
      
      return flashCardSet;
      
    } catch (error) {
      console.error('❌ Error generating flashcards with Bedrock:', error);
      
      if (error instanceof Error) {
        console.error('Error details:', {
          message: error.message,
          name: error.name,
          stack: error.stack
        });
      }
      
      // Fallback to local generation
      console.warn('🔄 Falling back to local flashcard generation due to Bedrock error');
      return this.createFallbackFlashCardSet(analysisResult);
    }
  }

  private buildFlashCardPrompt(analysisResult: AnalysisResult): string {
    const { analysis, threePrompts } = analysisResult;
    
    // Use Gemini's specific flashcard prompt from the three-prompt system, with fallback
    let geminiFlashCardPrompt: string;
    
    if (threePrompts && threePrompts.flashCardPrompt) {
      geminiFlashCardPrompt = threePrompts.flashCardPrompt;
    } else {
      // Fallback prompt for older sessions or missing data
      geminiFlashCardPrompt = `Create educational flashcards from the content about ${analysis.subject}. Each flashcard should have a clear question/term on the front and a comprehensive answer/definition on the back. Focus on key concepts, important terms, and critical information.`;
    }
    
    // Create structured JSON payload for Bedrock agent that incorporates Gemini's prompt
    const bedrockFlashCardPayload = {
      task: "GENERATE_MERMAID_FLASHCARDS",
      geminiPrompt: geminiFlashCardPrompt,
      analysis: analysis,
      extractedContent: {
        questions: analysis.extractedQuestions || [],
        patterns: analysis.questionPatterns || [],
        topics: analysis.topics,
        subject: analysis.subject,
        difficulty: analysis.difficulty
      },
      requirements: {
        cardCount: "15-25",
        includeMermaid: true,
        difficultyLevels: ['easy', 'medium', 'hard'],
        cardTypes: ['definition', 'concept', 'formula', 'process', 'diagram', 'example'],
        visualEnhancement: true,
        useGeminiPrompt: true
      },
      outputFormat: {
        type: "STRUCTURED_JSON",
        includeMermaidDiagrams: true,
        includeVisualElements: true,
        includeHints: true,
        includeRelatedConcepts: true
      }
    };

    return `
FLASHCARD GENERATION REQUEST:

Primary Prompt from Gemini Analysis:
${geminiFlashCardPrompt}

Additional Context:
${JSON.stringify(bedrockFlashCardPayload, null, 2)}

INSTRUCTIONS:
Use the Gemini prompt as your primary guide for content creation, and supplement it with the additional context provided. Generate high-quality interactive flashcards with Mermaid diagrams based on the combined analysis.

Key Requirements:
1. Follow the Gemini prompt guidelines for content and structure
2. Create 15-25 comprehensive flashcards covering all major topics
3. Include relevant Mermaid diagrams for visual learning
4. Use various diagram types: flowcharts, graphs, mind maps, sequences
5. Balance difficulty levels appropriately
6. Provide clear learning objectives and helpful hints
7. Ensure all Mermaid code is syntactically correct

Focus on creating flashcards that enhance understanding through visual learning and proper knowledge assessment while staying true to the Gemini prompt's intent.
    `.trim();
  }

  private parseFlashCardSet(
    content: string, 
    analysisResult: AnalysisResult, 
    selectedAgent: BedrockAgent
  ): BedrockFlashCardSet {
    try {
      // Try to extract JSON from the response
      const jsonMatch = content.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        const cleanedContent = jsonMatch[0]
          .replace(/```json\n?|\n?```/g, '')
          .replace(/```\n?|\n?```/g, '');
        
        const parsed = JSON.parse(cleanedContent);
        
        // Validate and enhance the parsed result
        return {
          title: parsed.title || `Interactive Flashcards for ${analysisResult.analysis.subject}`,
          subject: parsed.subject || analysisResult.analysis.subject,
          cards: this.validateAndEnhanceCards(parsed.cards || []),
          totalCards: parsed.totalCards || parsed.cards?.length || 0,
          estimatedStudyTime: parsed.estimatedStudyTime || this.calculateStudyTime(parsed.cards?.length || 0),
          difficultyDistribution: parsed.difficultyDistribution || this.calculateDifficultyDistribution(parsed.cards || []),
          topicCoverage: parsed.topicCoverage || analysisResult.analysis.topics,
          mermaidTypes: parsed.mermaidTypes || ['flowchart', 'graph', 'mindmap'],
          generatedBy: `Bedrock Agent: ${selectedAgent.name}`,
          timestamp: new Date().toISOString()
        };
      }
    } catch (parseError) {
      console.error('Failed to parse Bedrock flashcard response:', parseError);
    }
    
    // If parsing fails, create from analysis
    return this.createFallbackFlashCardSet(analysisResult);
  }

  private validateAndEnhanceCards(cards: any[]): BedrockFlashCard[] {
    return cards.map((card, index) => ({
      id: card.id || `bedrock_card_${index + 1}`,
      front: card.front || 'Question not available',
      back: card.back || 'Answer not available',
      topic: card.topic || 'General',
      difficulty: this.validateDifficulty(card.difficulty),
      type: this.validateCardType(card.type),
      mermaidDiagram: card.mermaidDiagram ? {
        type: this.validateMermaidType(card.mermaidDiagram.type),
        code: card.mermaidDiagram.code || '',
        description: card.mermaidDiagram.description || ''
      } : undefined,
      visualElements: {
        hasFormula: card.visualElements?.hasFormula || false,
        hasChart: card.visualElements?.hasChart || false,
        hasDiagram: card.visualElements?.hasDiagram || !!card.mermaidDiagram,
        complexityLevel: this.validateComplexityLevel(card.visualElements?.complexityLevel)
      },
      learningObjective: card.learningObjective || 'Understand the concept',
      hints: Array.isArray(card.hints) ? card.hints : [],
      relatedConcepts: Array.isArray(card.relatedConcepts) ? card.relatedConcepts : []
    }));
  }

  private validateDifficulty(difficulty: any): 'easy' | 'medium' | 'hard' {
    const validDifficulties = ['easy', 'medium', 'hard'];
    return validDifficulties.includes(difficulty) ? difficulty : 'medium';
  }

  private validateCardType(type: any): BedrockFlashCard['type'] {
    const validTypes: BedrockFlashCard['type'][] = ['definition', 'concept', 'formula', 'process', 'diagram', 'example'];
    return validTypes.includes(type) ? type : 'concept';
  }

  private validateMermaidType(type: any): MermaidDiagram['type'] {
    const validTypes: MermaidDiagram['type'][] = ['flowchart', 'graph', 'mindmap', 'sequence', 'class', 'state', 'pie', 'gitgraph'];
    return validTypes.includes(type) ? type : 'flowchart';
  }

  private validateComplexityLevel(level: any): 'simple' | 'moderate' | 'complex' {
    const validLevels = ['simple', 'moderate', 'complex'];
    return validLevels.includes(level) ? level : 'moderate';
  }

  private calculateStudyTime(cardCount: number): string {
    const minutesPerCard = 2;
    const totalMinutes = cardCount * minutesPerCard;
    
    if (totalMinutes < 60) {
      return `${totalMinutes} minutes`;
    } else {
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
    }
  }

  private calculateDifficultyDistribution(cards: any[]): { easy: number; medium: number; hard: number } {
    const distribution = { easy: 0, medium: 0, hard: 0 };
    
    cards.forEach(card => {
      const difficulty = this.validateDifficulty(card.difficulty);
      distribution[difficulty]++;
    });
    
    return distribution;
  }

  private createFallbackFlashCardSet(analysisResult: AnalysisResult): BedrockFlashCardSet {
    const { analysis } = analysisResult;
    const fallbackCards: BedrockFlashCard[] = [];
    
    // Create basic flashcards from topics
    analysis.topics.forEach((topic, index) => {
      const cardId = `fallback_card_${index + 1}`;
      
      // Create a definition card
      fallbackCards.push({
        id: cardId,
        front: `What is ${topic}?`,
        back: `${topic} is an important concept in ${analysis.subject}. It requires understanding of fundamental principles and practical applications.`,
        topic: topic,
        difficulty: this.mapDifficulty(analysis.difficulty),
        type: 'definition',
        mermaidDiagram: {
          type: 'mindmap',
          code: `mindmap\n  root((${topic}))\n    Concept\n      Definition\n      Examples\n    Application\n      Uses\n      Benefits`,
          description: `Mind map showing key aspects of ${topic}`
        },
        visualElements: {
          hasFormula: false,
          hasChart: false,
          hasDiagram: true,
          complexityLevel: 'simple'
        },
        learningObjective: `Understand the basic concept and applications of ${topic}`,
        hints: [`Think about how ${topic} relates to ${analysis.subject}`, 'Consider real-world examples'],
        relatedConcepts: analysis.topics.filter(t => t !== topic).slice(0, 2)
      });
    });
    
    // Add extracted questions as flashcards if available
    if (analysis.extractedQuestions && analysis.extractedQuestions.length > 0) {
      analysis.extractedQuestions.slice(0, 5).forEach((question, index) => {
        fallbackCards.push({
          id: `question_card_${index + 1}`,
          front: question,
          back: `This question tests understanding of key concepts in ${analysis.subject}. Consider the theoretical foundations and practical applications.`,
          topic: analysis.topics[index % analysis.topics.length] || 'General',
          difficulty: this.mapDifficulty(analysis.difficulty),
          type: 'example',
          visualElements: {
            hasFormula: question.toLowerCase().includes('formula') || question.toLowerCase().includes('equation'),
            hasChart: question.toLowerCase().includes('graph') || question.toLowerCase().includes('chart'),
            hasDiagram: false,
            complexityLevel: 'moderate'
          },
          learningObjective: 'Apply knowledge to solve problems',
          hints: ['Break down the question into parts', 'Identify key concepts involved'],
          relatedConcepts: analysis.topics.slice(0, 2)
        });
      });
    }

    return {
      title: `Flashcards for ${analysis.subject}`,
      subject: analysis.subject,
      cards: fallbackCards,
      totalCards: fallbackCards.length,
      estimatedStudyTime: this.calculateStudyTime(fallbackCards.length),
      difficultyDistribution: this.calculateDifficultyDistribution(fallbackCards),
      topicCoverage: analysis.topics,
      mermaidTypes: ['mindmap', 'flowchart'],
      generatedBy: 'Fallback Generator',
      timestamp: new Date().toISOString()
    };
  }

  private mapDifficulty(difficulty: string): 'easy' | 'medium' | 'hard' {
    switch (difficulty) {
      case 'easy': return 'easy';
      case 'medium': return 'medium';
      case 'hard':
      case 'expert': return 'hard';
      default: return 'medium';
    }
  }

  private cleanMixedContent(content: string): string {
    let cleaned = content;
    
    // Remove complete JSON objects and fragments
    cleaned = cleaned.replace(/\{[\s\S]*?"questionNumber"[\s\S]*?\}/g, ''); // Remove complete question objects
    cleaned = cleaned.replace(/\{[^}]*"questionNumber"[^}]*\}/g, ''); // Remove partial question objects
    cleaned = cleaned.replace(/\{[^}]*"question"[^}]*\}/g, ''); // Remove question JSON fragments
    cleaned = cleaned.replace(/\{[^}]*"marks"[^}]*\}/g, ''); // Remove marks JSON fragments
    cleaned = cleaned.replace(/\{[^}]*"questionType"[^}]*\}/g, ''); // Remove questionType JSON fragments
    cleaned = cleaned.replace(/\{[^}]*"options"[^}]*\}/g, ''); // Remove options JSON fragments
    cleaned = cleaned.replace(/\{[^}]*"correctAnswer"[^}]*\}/g, ''); // Remove correctAnswer JSON fragments
    cleaned = cleaned.replace(/\{[^}]*"answerKey"[^}]*\}/g, ''); // Remove answerKey JSON fragments
    cleaned = cleaned.replace(/\{[^}]*"bloomLevel"[^}]*\}/g, ''); // Remove bloomLevel JSON fragments
    
    // Remove JSON arrays and structures
    cleaned = cleaned.replace(/\[[^\]]*"[A-D]\)[^"]*"[^\]]*\]/g, ''); // Remove option arrays
    cleaned = cleaned.replace(/\[[^\]]*\]/g, ''); // Remove any remaining arrays
    cleaned = cleaned.replace(/\{[^}]*\}/g, ''); // Remove any remaining objects
    
    // Remove JSON syntax elements
    cleaned = cleaned.replace(/",\s*$/gm, ''); // Remove trailing commas and quotes
    cleaned = cleaned.replace(/^\s*"[^"]*":\s*/gm, ''); // Remove JSON keys
    cleaned = cleaned.replace(/^\s*[\[\{][^}\]]*$/gm, ''); // Remove incomplete opening brackets
    cleaned = cleaned.replace(/^\s*[}\]][^{\[]*$/gm, ''); // Remove closing brackets without opening
    cleaned = cleaned.replace(/^\s*"[^"]*"[,]?\s*$/gm, ''); // Remove standalone quoted strings
    
    // Remove specific problematic patterns from the user's example
    cleaned = cleaned.replace(/",\s*"bloomLevel":\s*"[^"]*"\s*\}/g, ''); // Remove bloomLevel endings
    cleaned = cleaned.replace(/\s*\{\s*$/gm, ''); // Remove standalone opening braces
    cleaned = cleaned.replace(/^\s*\}\s*,?\s*$/gm, ''); // Remove standalone closing braces
    
    // Clean up question formatting - extract actual questions from JSON fragments
    cleaned = cleaned.replace(/^\s*"question":\s*"([^"]*)"[,]?\s*$/gm, '$1'); // Extract question text from JSON
    cleaned = cleaned.replace(/^\s*"marks":\s*(\d+)[,]?\s*$/gm, '[$1 marks]'); // Convert marks to readable format
    cleaned = cleaned.replace(/^\s*"questionType":\s*"([^"]*)"[,]?\s*$/gm, '$1'); // Extract question type
    cleaned = cleaned.replace(/^\s*"correctAnswer":\s*"([^"]*)"[,]?\s*$/gm, 'Correct Answer: $1'); // Extract correct answer
    
    // Remove trailing quotes and commas that are leftover
    cleaned = cleaned.replace(/^\s*"[,]?\s*$/gm, ''); // Remove lines with just quotes
    cleaned = cleaned.replace(/",\s*$/gm, ''); // Remove trailing ", 
    
    // Remove empty lines and normalize whitespace
    cleaned = cleaned.replace(/^\s*$/gm, ''); // Remove empty lines
    cleaned = cleaned.replace(/\n\s*\n\s*\n/g, '\n\n'); // Remove multiple empty lines
    cleaned = cleaned.split('\n').filter(line => line.trim().length > 0).join('\n'); // Remove all empty lines
    
    return cleaned.trim();
  }

  // Helper method to extract topics from document content
  private extractTopicsFromContent(content: string): string[] {
    if (!content || content.length < 50) return [];
    
    const topics: string[] = [];
    const lines = content.split('\n').filter(line => line.trim().length > 10);
    
    // Extract headings and key terms
    for (const line of lines.slice(0, 20)) { // Check first 20 lines
      const trimmed = line.trim();
      
      // Look for potential headings or key concepts
      if (trimmed.length > 5 && trimmed.length < 100) {
        // Remove common prefixes
        let cleaned = trimmed
          .replace(/^\d+[\.\)\:]?\s*/, '')
          .replace(/^[A-Z][\.\)\:]?\s*/, '')
          .replace(/^\*+\s*/, '')
          .replace(/^#+\s*/, '')
          .trim();
        
        if (cleaned.length > 5 && cleaned.length < 80) {
          topics.push(cleaned);
        }
      }
    }
    
    return topics.slice(0, 8); // Return top 8 topics
  }

  // Generate questions based on actual topics
  private generateTopicBasedQuestion(topics: string[], subject: string, index: number, questionType: string, difficulty: string): any {
    const topic = topics[index % topics.length];
    const isMultipleChoice = questionType.toLowerCase().includes('mcq') || questionType.toLowerCase().includes('multiple choice');
    
    if (isMultipleChoice) {
      const questionTemplates = [
        {
          question: `Based on the content, what is the primary focus of "${topic}"?`,
          options: [
            `A) Core concepts and practical applications`,
            `B) Theoretical overview only`,
            `C) Historical background`,
            `D) Unrelated information`
          ],
          correctAnswer: "A"
        },
        {
          question: `In the context of "${topic}", which statement is most accurate?`,
          options: [
            `A) It represents important principles in ${subject}`,
            `B) It is irrelevant to the subject matter`,
            `C) It contains outdated information`,
            `D) It should be ignored for study purposes`
          ],
          correctAnswer: "A"
        },
        {
          question: `How does "${topic}" relate to the overall ${subject} content?`,
          options: [
            `A) It provides essential knowledge and understanding`,
            `B) It has no connection to the main content`,
            `C) It contradicts other concepts`,
            `D) It is purely supplementary material`
          ],
          correctAnswer: "A"
        }
      ];
      
      return questionTemplates[index % questionTemplates.length];
    } else {
      const openQuestions = [
        `Explain the key concepts presented in "${topic}" and their significance in ${subject}.`,
        `Analyze the importance of "${topic}" and discuss its practical applications.`,
        `Describe the main principles covered in "${topic}" and how they relate to ${subject}.`,
        `Evaluate the content of "${topic}" and its relevance to understanding ${subject}.`
      ];
      
      return { question: openQuestions[index % openQuestions.length] };
    }
  }

  // Generate contextual questions when topics are limited
  private generateContextualQuestion(subject: string, index: number, questionType: string, difficulty: string): any {
    const isMultipleChoice = questionType.toLowerCase().includes('mcq') || questionType.toLowerCase().includes('multiple choice');
    
    if (isMultipleChoice) {
      const questions = [
        {
          question: `What approach should be taken when studying ${subject} materials?`,
          options: [
            `A) Systematic understanding of key concepts`,
            `B) Memorization without comprehension`,
            `C) Skipping difficult topics`,
            `D) Focusing only on surface-level information`
          ],
          correctAnswer: "A"
        },
        {
          question: `Which skill is most important for mastering ${subject}?`,
          options: [
            `A) Critical thinking and practical application`,
            `B) Rote memorization`,
            `C) Avoiding complex problems`,
            `D) Superficial reading`
          ],
          correctAnswer: "A"
        },
        {
          question: `What characterizes effective learning in ${subject}?`,
          options: [
            `A) Understanding fundamentals and their applications`,
            `B) Memorizing facts without context`,
            `C) Avoiding challenging concepts`,
            `D) Relying on shortcuts only`
          ],
          correctAnswer: "A"
        }
      ];
      
      return questions[index % questions.length];
    } else {
      const questions = [
        `Discuss the fundamental principles that are essential for understanding ${subject}.`,
        `Explain the methodology and approach used in ${subject} studies.`,
        `Analyze the key concepts that form the foundation of ${subject}.`,
        `Evaluate the importance of systematic learning in ${subject}.`
      ];
      
      return { question: questions[index % questions.length] };
    }
  }

  // Generate a default question as last resort
  private generateDefaultQuestion(subject: string, index: number, questionType: string): any {
    const isMultipleChoice = questionType.toLowerCase().includes('mcq') || questionType.toLowerCase().includes('multiple choice');
    
    if (isMultipleChoice) {
      return {
        question: `What is an important aspect of ${subject} education?`,
        options: [
          `A) Comprehensive understanding and practical knowledge`,
          `B) Surface-level information only`,
          `C) Avoiding detailed study`,
          `D) Memorizing without understanding`
        ],
        correctAnswer: "A"
      };
    } else {
      return {
        question: `Explain the importance of systematic study in ${subject} and its practical applications.`
      };
    }
  }
}

export const bedrockService = new BedrockService();
